import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import PauseCircleRoundedIcon from '@mui/icons-material/PauseCircleRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import FingerprintRoundedIcon from '@mui/icons-material/FingerprintRounded';
import ShieldMoonRoundedIcon from '@mui/icons-material/ShieldMoonRounded';
import ShareRoundedIcon from '@mui/icons-material/ShareRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import WorkRoundedIcon from '@mui/icons-material/WorkRounded';
import { useLocation, useParams } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import AppNavbar from '../components/AppNavbar';
import ClassificationBanner from '../components/ClassificationBanner';
import Header from '../components/Header';
import SideMenu from '../components/SideMenu';
import AppTheme from '../shared-theme/AppTheme';
import axiosInstance from '../utils/axiosInstance';
import RequestWizard from '../components/RequestWizard';
import ThreadCardsView from '../components/workspace/cards/ThreadCardsView';
import WorkstreamTasks from '../components/workstream/tasks/WorkstreamTasks';
import CapabilityFulfillmentPanel from '../components/workspace/CapabilityFulfillmentPanel';
import AssetRunCard from '../components/AssetRunCard';
import { formatRunDuration, isRunActive, runStatusColor } from '../utils/assetRuns';
import {
  formatDateTime,
  getPartnerDisplayName,
} from '../components/workspace/messages/messageUtils';

import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from './theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

const formatDate = (value) => {
  if (!value) {
    return 'Unknown';
  }

  return new Date(value).toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const capabilityOptionLabel = (capability = {}) => (
  capability?.capability_name ||
  capability?.source_capability_definition_id ||
  capability?.capability_definition_id ||
  'Capability'
);

const assetOptionLabel = (asset = {}) => (
  asset?.asset_name ||
  asset?.source_capability_asset_id ||
  asset?.asset_id ||
  'Asset'
);

const emptyShareForm = {
  targetMode: 'internal',
  targetUser: null,
  targetSearch: '',
  targetEmail: '',
  permission: 'view',
  expiresAt: '',
  excludedCorrIds: [],
  redactedFields: [],
  redactedFileIds: [],
};

const getSchemaFieldName = (field = {}) => (
  field?.name || field?.field || field?.key || field?.id || ''
);

const getSchemaFieldLabel = (field = {}) => (
  field?.label || field?.title || field?.display_name || getSchemaFieldName(field)
);

const getCardSchemaFields = (card = {}) => (
  Array.isArray(card?.schema?.body)
    ? card.schema.body.filter((field) => getSchemaFieldName(field))
    : []
);

const getCardFiles = (card = {}) => (
  Array.isArray(card?.files) ? card.files : []
);

const formatDirectLabel = (value) => String(value || 'unknown')
  .split(/[_\s-]+/)
  .filter(Boolean)
  .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
  .join(' ');

const directBusinessUnitLabel = (unit = {}) => {
  const parent = unit?.parent_partner?.display_name || unit?.parent_partner?.partner_code;
  const name = unit?.display_name || unit?.business_unit_code || unit?.business_unit_id || 'Business Unit';
  return parent ? `${parent} / ${name}` : name;
};

const directFieldType = (field = {}) => String(field?.type || 'text').toLowerCase();

const formatDirectValue = (value) => {
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (Array.isArray(value)) {
    return value.map(formatDirectValue).join(', ');
  }
  if (value && typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }
  return String(value ?? 'N/A');
};

const directStatusColor = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'completed') {
    return 'success';
  }
  if (normalized === 'failed' || normalized === 'declined' || normalized === 'cancelled') {
    return 'error';
  }
  if (normalized === 'waiting' || normalized === 'awaiting_resource') {
    return 'warning';
  }
  if (normalized === 'accepted' || normalized === 'in_progress') {
    return 'info';
  }
  return 'default';
};

const automatedWorkstreamWithoutExecution = (workstream = {}) => {
  const mode = workstream.fulfillment_mode || workstream.capability_snapshot?.fulfillment_mode;
  const runs = Array.isArray(workstream.runs) ? workstream.runs : [];
  return mode === 'automated' && workstream.status === 'accepted' && runs.length === 0;
};

const directOutputKey = (output = {}) => (
  output?.key || output?.output_key || output?.output_id || output?.name || ''
);

const directUserLabel = (user = {}) => (
  user?.full_name || user?.email || user?.cognito_sub || user?.user_id || 'Unassigned'
);

const directNeededBy = (request = {}) => (
  request?.metadata?.needed_by ||
  request?.metadata?.needed_by_date ||
  request?.metadata?.needed_by_at ||
  request?.metadata?.required_by ||
  ''
);

const directLatestRun = (workstream = {}) => (
  [...(Array.isArray(workstream.runs) ? workstream.runs : [])]
    .sort((left, right) => new Date(
      right?.started_at || right?.created_at || 0,
    ) - new Date(left?.started_at || left?.created_at || 0))[0] || null
);

const directRunLabel = (run = {}) => (
  run?.asset?.asset_name || run?.run_name || `Run ${String(run?.run_id || '').slice(0, 8)}` || 'Asset Run'
);

const directRequestTypeLabel = (request = {}) => {
  const type = request?.request_type || request?.type || 'direct_request';
  return type === 'capability_based' || type === 'direct_request'
    ? 'Direct capability request'
    : formatDirectLabel(type);
};

const directDayLabel = (value) => {
  if (!value) return 'Unknown date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
};

const directTimeLabel = (value) => {
  if (!value) return 'Unknown time';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const DirectEmptyState = ({ title, description }) => (
  <Box
    sx={{
      px: 2,
      py: 2.5,
      borderRadius: 2,
      border: (theme) => `1px dashed ${alpha(theme.palette.divider, 0.9)}`,
      bgcolor: (theme) => alpha(theme.palette.action.hover, 0.32),
      textAlign: 'center',
    }}
  >
    <Typography variant="body2" sx={{ fontWeight: 750 }}>{title}</Typography>
    {description ? (
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.4 }}>
        {description}
      </Typography>
    ) : null}
  </Box>
);

const shareTargetLabel = (target = {}) => {
  const name = target?.full_name || target?.email || 'AMX user';
  const partner = target?.partner_display_name || target?.partner_code;
  return partner ? `${name} - ${partner}` : name;
};

const directArtifactsForField = (artifacts = [], source, fieldKey, value) => {
  const ids = new Set((Array.isArray(value) ? value : [value]).map((item) => String(item || '')).filter(Boolean));
  return artifacts.filter((artifact) => (
    artifact.source === source &&
    artifact.field_key === fieldKey &&
    (!ids.size || ids.has(artifact.file_id) || ids.has(artifact.artifact_id))
  ));
};

function DirectValueRows({ title, fields, values, artifacts, source, onDownload, downloadingArtifact }) {
  const rows = useMemo(() => {
    const fieldsByKey = new Map((fields || []).map((field) => [getSchemaFieldName(field), field]));
    return Object.entries(values || {}).map(([key, value]) => ({
      key,
      field: fieldsByKey.get(key),
      value,
    }));
  }, [fields, values]);

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 850, mb: 1 }}>
        {title}
      </Typography>
      <Stack spacing={1}>
        {rows.length ? rows.map((row) => {
          const type = directFieldType(row.field);
          const fieldArtifacts = type === 'file'
            ? directArtifactsForField(artifacts, source, row.key, row.value)
            : [];
          return (
            <Box
              key={row.key}
              sx={{
                p: 1.25,
                borderRadius: 1.5,
                border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.75)}`,
                bgcolor: 'background.paper',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {getSchemaFieldLabel(row.field || { key: row.key })}
              </Typography>
              {type === 'file' && fieldArtifacts.length ? (
                <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                  {fieldArtifacts.map((artifact) => (
                    <Stack
                      key={artifact.file_id}
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      justifyContent="space-between"
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                          {artifact.filename || artifact.file_name || artifact.file_id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {artifact.size_bytes ?? 0} bytes
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        startIcon={<DownloadRoundedIcon />}
                        onClick={() => onDownload(artifact)}
                        disabled={downloadingArtifact === artifact.file_id}
                      >
                        {downloadingArtifact === artifact.file_id ? 'Downloading...' : 'Download'}
                      </Button>
                    </Stack>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', mt: 0.35 }}>
                  {formatDirectValue(row.value)}
                </Typography>
              )}
            </Box>
          );
        }) : (
          <Typography variant="body2" color="text.secondary">
            No values submitted.
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

function DirectOutputs({
  workstream,
  onProvideOutput,
  onDownloadOutput,
  busyKey,
}) {
  const outputs = workstream?.expected_outputs || [];
  const authorization = workstream?.authorization || {};
  const normalized = Array.isArray(outputs) ? outputs : [];
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 850, mb: 1 }}>
        Expected Outputs
      </Typography>
      <Stack spacing={1}>
        {normalized.length ? normalized.map((output) => (
          <Box
            key={output.output_id || output.key || output.name}
            sx={{
              p: 1.25,
              borderRadius: 1.5,
              border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.75)}`,
              bgcolor: 'background.paper',
            }}
          >
            <Stack spacing={1}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 750 }}>
                    {output.name || formatDirectLabel(directOutputKey(output))}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDirectLabel(output.kind)} / {output.required ? 'Required' : 'Optional'}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  color={output.fulfilled ? 'success' : output.required ? 'warning' : 'default'}
                  label={output.fulfilled ? 'Provided' : 'Not Provided'}
                  variant={output.fulfilled ? 'filled' : 'outlined'}
                />
              </Stack>
              {output.fulfillment && String(output.kind || '').toLowerCase() === 'asset_run' ? (
                <Stack spacing={1}>
                  {(output.fulfillment.runs || []).map((run) => (
                    <AssetRunCard key={run.run_id} run={run} compact />
                  ))}
                </Stack>
              ) : output.fulfillment ? (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                      {output.fulfillment.filename || output.fulfillment.file_name || output.fulfillment.value || 'Output provided'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {directUserLabel(output.fulfillment.provided_by)} / {formatDate(output.fulfillment.provided_at)}
                    </Typography>
                  </Box>
                  {output.fulfillment.artifact_id ? (
                    <Button
                      size="small"
                      startIcon={<DownloadRoundedIcon />}
                      onClick={() => onDownloadOutput(output)}
                      disabled={busyKey === `download-output:${directOutputKey(output)}`}
                    >
                      {busyKey === `download-output:${directOutputKey(output)}` ? 'Downloading...' : 'Download'}
                    </Button>
                  ) : null}
                </Stack>
              ) : null}
              {authorization.can_provide_output ? (
                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<UploadFileRoundedIcon />}
                    onClick={() => onProvideOutput(output)}
                    disabled={busyKey === `output:${directOutputKey(output)}`}
                  >
                    Add Output
                  </Button>
                </Stack>
              ) : null}
            </Stack>
          </Box>
        )) : (
          <Typography variant="body2" color="text.secondary">
            No expected outputs.
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

function DirectRequestWorkspaceContent({
  workspaceData,
  onRefresh,
  initialWorkstreamId = '',
  initialTaskId = '',
}) {
  const workspace = workspaceData?.workspace || {};
  const request = workspaceData?.request || {};
  const participants = Array.isArray(workspaceData?.participants) ? workspaceData.participants : [];
  const workstreams = Array.isArray(workspaceData?.workstreams) ? workspaceData.workstreams : [];
  const artifacts = Array.isArray(workspaceData?.artifacts) ? workspaceData.artifacts : [];
  const generalArtifacts = Array.isArray(workspaceData?.general_artifacts) ? workspaceData.general_artifacts : [];
  const messages = Array.isArray(workspaceData?.messages) ? workspaceData.messages : [];
  const activity = Array.isArray(workspaceData?.activity) ? workspaceData.activity : [];
  const [tab, setTab] = useState(initialWorkstreamId ? 'work' : 'overview');
  const [selectedWorkstreamId, setSelectedWorkstreamId] = useState(
    initialWorkstreamId || workstreams[0]?.request_capability_id || '',
  );
  const [downloadError, setDownloadError] = useState('');
  const [downloadingArtifact, setDownloadingArtifact] = useState('');
  const [workActionBusy, setWorkActionBusy] = useState('');
  const [workActionError, setWorkActionError] = useState('');
  const [workActionSuccess, setWorkActionSuccess] = useState('');
  const [waitDialog, setWaitDialog] = useState({
    open: false,
    workstreamId: '',
    waitingOn: 'requester',
    reason: '',
  });
  const [failDialog, setFailDialog] = useState({
    open: false,
    workstreamId: '',
    reason: '',
  });
  const [outputDialog, setOutputDialog] = useState({
    open: false,
    workstreamId: '',
    output: null,
    file: null,
    description: '',
    runIds: [],
  });
  const [artifactUpload, setArtifactUpload] = useState({
    file: null,
    scope: 'request_capability',
  });
  const [messageDraft, setMessageDraft] = useState('');
  const [selectedMessageChannelId, setSelectedMessageChannelId] = useState('general');
  const handledInitialNavigation = useRef('');

  useEffect(() => {
    if (!selectedWorkstreamId && workstreams[0]?.request_capability_id) {
      setSelectedWorkstreamId(workstreams[0].request_capability_id);
    }
  }, [selectedWorkstreamId, workstreams]);

  useEffect(() => {
    const targetKey = `${initialWorkstreamId}:${initialTaskId}`;
    if (
      initialWorkstreamId &&
      handledInitialNavigation.current !== targetKey &&
      workstreams.some((workstream) => workstream.request_capability_id === initialWorkstreamId)
    ) {
      handledInitialNavigation.current = targetKey;
      setSelectedWorkstreamId(initialWorkstreamId);
      setTab('work');
    }
  }, [initialTaskId, initialWorkstreamId, workstreams]);

  const selectedWorkstream = workstreams.find((item) => item.request_capability_id === selectedWorkstreamId) || workstreams[0] || {};
  const requestStatus = request.status || selectedWorkstream.status || workspace.status || 'unknown';
  const neededBy = directNeededBy(request);
  const outputArtifactCount = workstreams.reduce((count, workstream) => (
    count + (workstream.expected_outputs || []).filter((output) => output.fulfillment?.artifact_id).length
  ), 0);
  const artifactCount = artifacts.length + generalArtifacts.length + outputArtifactCount;
  const allRuns = workstreams.flatMap((workstream) => workstream.runs || []);
  const activeRunCount = allRuns.filter(isRunActive).length;
  const attentionCount = workstreams.filter((workstream) => (
    ['waiting', 'awaiting_resource', 'failed'].includes(String(workstream.status || '').toLowerCase())
  )).length;
  const lastUpdated = [
    workspace.updated_at,
    request.updated_at,
    ...workstreams.map((workstream) => workstream.updated_at),
    ...messages.map((message) => message.created_at),
    ...activity.map((event) => event.timestamp),
  ].filter(Boolean).sort((left, right) => new Date(right) - new Date(left))[0];

  const messageChannels = useMemo(() => ([
    {
      type: 'workspace',
      id: 'general',
      label: 'General Workspace',
      workspaceId: workspace.workspace_id,
    },
    ...workstreams.map((workstream) => ({
      type: 'workstream',
      id: workstream.request_capability_id,
      label: workstream.capability_name || 'Requested Capability',
      workspaceId: workspace.workspace_id,
      workstreamId: workstream.request_capability_id,
      capabilityName: workstream.capability_name,
      providerName: directBusinessUnitLabel(workstream.provider_business_unit),
      status: workstream.status,
    })),
  ]), [workspace.workspace_id, workstreams]);

  const selectedMessageChannel = messageChannels.find((channel) => channel.id === selectedMessageChannelId)
    || messageChannels[0];
  const selectedMessageWorkstream = selectedMessageChannel?.type === 'workstream'
    ? workstreams.find((workstream) => workstream.request_capability_id === selectedMessageChannel.workstreamId)
    : null;

  useEffect(() => {
    const hasActiveRuns = workstreams.some((workstream) => (workstream.runs || []).some(isRunActive));
    if (!hasActiveRuns || typeof onRefresh !== 'function') return undefined;
    const timer = window.setInterval(() => onRefresh(), 15000);
    return () => window.clearInterval(timer);
  }, [onRefresh, workstreams]);

  const openWorkstream = (workstream) => {
    setSelectedWorkstreamId(workstream.request_capability_id);
    setTab('work');
  };

  const openWorkstreamMessages = (workstream) => {
    setSelectedWorkstreamId(workstream.request_capability_id);
    setSelectedMessageChannelId(workstream.request_capability_id);
    setTab('messages');
  };

  const triggerDownload = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    if (filename) {
      link.download = filename;
    }
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadArtifact = async (artifact = {}) => {
    const fileId = artifact.file_id || artifact.artifact_id;
    const requestCapabilityId = artifact.request_capability_id || selectedWorkstream.request_capability_id;
    if (!fileId || !requestCapabilityId) {
      setDownloadError('Artifact download context is missing.');
      return;
    }

    setDownloadingArtifact(fileId);
    setDownloadError('');
    try {
      const response = await axiosInstance.post(
        `/requests/direct/${encodeURIComponent(requestCapabilityId)}/artifacts/${encodeURIComponent(fileId)}/download`,
      );
      const url = response.data?.download_url || response.data?.url;
      if (!url) {
        throw new Error('Download URL was not returned.');
      }
      triggerDownload(url, response.data?.filename || artifact.filename || artifact.file_name);
    } catch (err) {
      setDownloadError(err?.response?.data?.message || err?.message || 'Unable to download artifact.');
    } finally {
      setDownloadingArtifact('');
    }
  };

  const refreshDirectWorkspace = async () => {
    if (typeof onRefresh === 'function') {
      await onRefresh();
    }
  };

  const postWorkAction = async (workstream, action, body = {}) => {
    const requestCapabilityId = workstream?.request_capability_id;
    if (!requestCapabilityId) {
      setWorkActionError('Missing workstream context.');
      return;
    }
    const busyKey = `${requestCapabilityId}:${action}`;
    setWorkActionBusy(busyKey);
    setWorkActionError('');
    setWorkActionSuccess('');
    try {
      await axiosInstance.post(
        `/requests/direct/${encodeURIComponent(requestCapabilityId)}/${action}`,
        body,
      );
      setWorkActionSuccess(formatDirectLabel(`work ${action}`));
      await refreshDirectWorkspace();
      return true;
    } catch (err) {
      setWorkActionError(err?.response?.data?.message || err?.message || `Unable to ${action} workstream.`);
      return false;
    } finally {
      setWorkActionBusy('');
    }
  };

  const handleWaitSubmit = async () => {
    const workstream = workstreams.find((item) => item.request_capability_id === waitDialog.workstreamId);
    const okToClose = await postWorkAction(workstream, 'wait', {
      waiting_on: waitDialog.waitingOn,
      waiting_reason: waitDialog.reason,
    });
    if (okToClose) {
      setWaitDialog({ open: false, workstreamId: '', waitingOn: 'requester', reason: '' });
    }
  };

  const handleFailSubmit = async () => {
    const workstream = workstreams.find((item) => item.request_capability_id === failDialog.workstreamId);
    const okToClose = await postWorkAction(workstream, 'fail', {
      failure_reason: failDialog.reason,
    });
    if (okToClose) {
      setFailDialog({ open: false, workstreamId: '', reason: '' });
    }
  };

  const handleProvideOutput = (output) => {
    setOutputDialog({
      open: true,
      workstreamId: selectedWorkstream.request_capability_id,
      output,
      file: null,
      description: '',
      runIds: output?.fulfillment?.run_ids || [],
    });
    setWorkActionError('');
    setWorkActionSuccess('');
  };

  const handleOutputSubmit = async () => {
    const output = outputDialog.output || {};
    const outputKey = directOutputKey(output);
    if (!outputDialog.workstreamId || !outputKey) {
      setWorkActionError('Missing output context.');
      return;
    }
    if (String(output.kind || 'file').toLowerCase() === 'file' && !outputDialog.file) {
      setWorkActionError('Choose a file before adding the output.');
      return;
    }
    if (String(output.kind || '').toLowerCase() === 'asset_run' && !outputDialog.runIds.length) {
      setWorkActionError('Select at least one Run before adding the output.');
      return;
    }

    const busyKey = `output:${outputKey}`;
    setWorkActionBusy(busyKey);
    setWorkActionError('');
    setWorkActionSuccess('');
    try {
      const file = outputDialog.file;
      const outputKind = String(output.kind || 'file').toLowerCase();
      const response = await axiosInstance.post(
        `/requests/direct/${encodeURIComponent(outputDialog.workstreamId)}/outputs/${encodeURIComponent(outputKey)}`,
        outputKind === 'asset_run' ? {
          run_ids: outputDialog.runIds,
          description: outputDialog.description.trim() || undefined,
        } : {
          filename: file?.name,
          content_type: file?.type || 'application/octet-stream',
          size_bytes: file?.size,
          description: outputDialog.description.trim() || undefined,
        },
      );
      const uploadUrl = response.data?.upload_url || response.data?.url;
      if (uploadUrl && file) {
        const uploadResponse = await fetch(uploadUrl, {
          method: response.data?.method || 'PUT',
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          body: file,
        });
        if (!uploadResponse.ok) {
          throw new Error(`Upload failed with status ${uploadResponse.status}.`);
        }
      }
      setOutputDialog({ open: false, workstreamId: '', output: null, file: null, description: '', runIds: [] });
      setWorkActionSuccess(`${output.name || formatDirectLabel(outputKey)} provided.`);
      await refreshDirectWorkspace();
    } catch (err) {
      setWorkActionError(err?.response?.data?.message || err?.message || 'Unable to provide output.');
    } finally {
      setWorkActionBusy('');
    }
  };

  const handleDownloadOutput = async (output = {}) => {
    const outputKey = directOutputKey(output);
    if (!selectedWorkstream.request_capability_id || !outputKey) {
      setWorkActionError('Missing output download context.');
      return;
    }
    const busyKey = `download-output:${outputKey}`;
    setWorkActionBusy(busyKey);
    setWorkActionError('');
    try {
      const response = await axiosInstance.post(
        `/requests/direct/${encodeURIComponent(selectedWorkstream.request_capability_id)}/outputs/${encodeURIComponent(outputKey)}/download`,
      );
      const url = response.data?.download_url || response.data?.url;
      if (!url) {
        throw new Error('Download URL was not returned.');
      }
      triggerDownload(url, response.data?.filename || output.fulfillment?.filename || output.name);
    } catch (err) {
      setWorkActionError(err?.response?.data?.message || err?.message || 'Unable to download output.');
    } finally {
      setWorkActionBusy('');
    }
  };

  const handleArtifactUpload = async () => {
    if (!selectedWorkstream.request_capability_id) {
      setWorkActionError('Select a workstream before uploading an artifact.');
      return;
    }
    if (!artifactUpload.file) {
      setWorkActionError('Choose a file before uploading an artifact.');
      return;
    }

    const busyKey = `artifact:${selectedWorkstream.request_capability_id}`;
    setWorkActionBusy(busyKey);
    setWorkActionError('');
    setWorkActionSuccess('');
    try {
      const response = await axiosInstance.post(
        `/requests/direct/${encodeURIComponent(selectedWorkstream.request_capability_id)}/artifacts`,
        {
          filename: artifactUpload.file.name,
          content_type: artifactUpload.file.type || 'application/octet-stream',
          size_bytes: artifactUpload.file.size,
          scope: artifactUpload.scope,
        },
      );
      const uploadUrl = response.data?.upload_url || response.data?.url;
      if (!uploadUrl) {
        throw new Error('Upload URL was not returned.');
      }
      const uploadResponse = await fetch(uploadUrl, {
        method: response.data?.method || 'PUT',
        headers: { 'Content-Type': artifactUpload.file.type || 'application/octet-stream' },
        body: artifactUpload.file,
      });
      if (!uploadResponse.ok) {
        throw new Error(`Upload failed with status ${uploadResponse.status}.`);
      }
      setArtifactUpload({ file: null, scope: 'request_capability' });
      setWorkActionSuccess(`${artifactUpload.file.name} uploaded.`);
      await refreshDirectWorkspace();
    } catch (err) {
      setWorkActionError(err?.response?.data?.message || err?.message || 'Unable to upload artifact.');
    } finally {
      setWorkActionBusy('');
    }
  };

  const handleSendMessage = async () => {
    const body = messageDraft.trim();
    if (!body) {
      setWorkActionError('Enter a message before sending.');
      return;
    }
    const targetWorkstream = selectedMessageChannel?.type === 'workstream'
      ? selectedMessageWorkstream
      : workstreams.find((workstream) => workstream.authorization?.can_send_message);
    if (!targetWorkstream?.request_capability_id) {
      setWorkActionError('You do not have access to send a message to this channel.');
      return;
    }

    const busyKey = `message:${selectedMessageChannel.id}`;
    setWorkActionBusy(busyKey);
    setWorkActionError('');
    setWorkActionSuccess('');
    try {
      await axiosInstance.post(
        `/requests/direct/${encodeURIComponent(targetWorkstream.request_capability_id)}/messages`,
        {
          body,
          scope: selectedMessageChannel.type === 'workspace' ? 'workspace' : 'request_capability',
        },
      );
      setMessageDraft('');
      setWorkActionSuccess(`Message sent to ${selectedMessageChannel.label}.`);
      await refreshDirectWorkspace();
    } catch (err) {
      setWorkActionError(err?.response?.data?.message || err?.message || 'Unable to send message.');
    } finally {
      setWorkActionBusy('');
    }
  };

  const renderWorkstreamCard = (workstream) => {
    const latestRun = directLatestRun(workstream);
    const fulfillmentMode = workstream.fulfillment_mode || workstream.capability_snapshot?.fulfillment_mode || 'manual';
    const workstreamArtifactCount = (workstream.artifacts || []).length
      + (workstream.expected_outputs || []).filter((output) => output.fulfillment?.artifact_id).length;
    return (
      <Card
        key={workstream.request_capability_id}
        variant="outlined"
        sx={{
          borderRadius: 2,
          boxShadow: 'none',
          transition: (theme) => theme.transitions.create(['border-color', 'box-shadow']),
          '&:hover': { borderColor: 'primary.light', boxShadow: 1 },
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Stack spacing={1.35}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between">
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 850, wordBreak: 'break-word' }}>
                  {workstream.capability_name || 'Requested Capability'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {directBusinessUnitLabel(workstream.provider_business_unit)}
                </Typography>
              </Box>
              <Chip size="small" color={directStatusColor(workstream.status)} label={formatDirectLabel(workstream.status)} />
            </Stack>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              <Chip size="small" variant="outlined" label={`${formatDirectLabel(fulfillmentMode)} fulfillment`} />
              <Chip size="small" variant="outlined" label={`${(workstream.runs || []).length} run${(workstream.runs || []).length === 1 ? '' : 's'}`} />
              <Chip size="small" variant="outlined" label={`${workstreamArtifactCount} artifact${workstreamArtifactCount === 1 ? '' : 's'}`} />
            </Stack>
            {latestRun ? (
              <Box
                sx={{
                  p: 1.25,
                  borderRadius: 1.5,
                  bgcolor: (theme) => alpha(theme.palette.action.hover, 0.45),
                }}
              >
                <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary">Latest run</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 750 }} noWrap>{directRunLabel(latestRun)}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Started {formatDate(latestRun.started_at)} · {formatRunDuration(latestRun.duration_seconds)}
                      {Number.isFinite(Number(latestRun.progress ?? latestRun.progress_percent))
                        ? ` · ${Math.round(Number(latestRun.progress ?? latestRun.progress_percent))}%`
                        : ''}
                    </Typography>
                  </Box>
                  <Chip size="small" color={runStatusColor(latestRun.status)} label={formatDirectLabel(latestRun.status)} />
                </Stack>
              </Box>
            ) : null}
            <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
              <Button size="small" startIcon={<ForumRoundedIcon />} onClick={() => openWorkstreamMessages(workstream)}>
                Messages
              </Button>
              <Button size="small" variant="outlined" onClick={() => openWorkstream(workstream)}>
                Open
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const renderOverview = () => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.35fr) minmax(280px, 0.65fr)' },
        gap: 2,
        alignItems: 'start',
      }}
    >
      <Stack spacing={2}>
        <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 'none' }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Stack spacing={1.5}>
              <Typography variant="subtitle1" sx={{ fontWeight: 850 }}>Request summary</Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                  gap: 1.5,
                }}
              >
                {[
                  ['Request title', request.title || workspace.title || 'Workspace request'],
                  ['Request type', directRequestTypeLabel(request)],
                  ['Requestor', directBusinessUnitLabel(workspaceData?.requesting_business_unit)],
                  ['Status', formatDirectLabel(requestStatus)],
                  ['Needed by', neededBy ? formatDate(neededBy) : 'Not specified'],
                  ['Created', formatDate(request.created_at || workspace.created_at)],
                ].map(([label, value]) => (
                  <Box key={label} sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>{value}</Typography>
                  </Box>
                ))}
              </Box>
              {request.description ? (
                <Box sx={{ pt: 1.25, borderTop: (theme) => `1px solid ${theme.palette.divider}` }}>
                  <Typography variant="caption" color="text.secondary">Need statement</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.35, lineHeight: 1.6 }}>
                    {request.description}
                  </Typography>
                </Box>
              ) : null}
            </Stack>
          </CardContent>
        </Card>
        <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 'none' }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 850 }}>Provider workstreams</Typography>
                <Typography variant="caption" color="text.secondary">
                  Capabilities are fulfilled independently and may run in parallel.
                </Typography>
              </Box>
              {workstreams.length ? workstreams.map((workstream) => renderWorkstreamCard(workstream)) : (
                <DirectEmptyState
                  title="No provider workstreams yet"
                  description="Accepted capabilities and their fulfillment status will appear here."
                />
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>
      <Stack spacing={2}>
        <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 'none' }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Stack spacing={1.5}>
              <Typography variant="subtitle1" sx={{ fontWeight: 850 }}>Participants</Typography>
              {['requester', 'provider'].map((role) => {
                const roleParticipants = participants.filter((participant) => participant.role === role);
                if (!roleParticipants.length) return null;
                return (
                  <Box key={role}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 750 }}>
                      {role === 'requester' ? 'Requestor' : 'Providers'}
                    </Typography>
                    <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                      {roleParticipants.map((participant) => (
                        <Typography key={`${participant.role}:${participant.business_unit_id}`} variant="body2" sx={{ fontWeight: 700 }}>
                          {directBusinessUnitLabel(participant)}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </CardContent>
        </Card>
        <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 'none' }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Stack spacing={1.25}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1" sx={{ fontWeight: 850 }}>Recent activity</Typography>
                <Button size="small" onClick={() => setTab('activity')}>View all</Button>
              </Stack>
              {activity.slice(0, 5).map((event, index) => (
                <Stack key={event.activity_id} direction="row" spacing={1.25}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box sx={{ width: 8, height: 8, mt: 0.65, borderRadius: '50%', bgcolor: 'primary.main' }} />
                    {index < Math.min(activity.length, 5) - 1 ? <Box sx={{ width: 1, flex: 1, minHeight: 24, bgcolor: 'divider' }} /> : null}
                  </Box>
                  <Box sx={{ minWidth: 0, pb: 0.75 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{event.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{formatDate(event.timestamp)}</Typography>
                  </Box>
                </Stack>
              ))}
              {!activity.length ? (
                <DirectEmptyState title="No activity yet" description="Workspace events will appear here as work begins." />
              ) : null}
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );

  const renderWork = () => {
    const snapshot = selectedWorkstream.capability_snapshot || {};
    const fulfillmentMode = selectedWorkstream.fulfillment_mode || snapshot.fulfillment_mode || 'manual';
    const automatedFulfillment = fulfillmentMode === 'automated';
    return (
      <Stack spacing={2}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 850 }}>Workstreams</Typography>
          <Typography variant="body2" color="text.secondary">
            Review each provider engagement independently, including its execution, inputs, and outputs.
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' },
            gap: 1.5,
          }}
        >
          {workstreams.map((workstream) => renderWorkstreamCard(workstream))}
        </Box>
        {!workstreams.length ? (
          <DirectEmptyState title="No workstreams yet" description="Provider work will appear here after a capability is accepted." />
        ) : null}
        {selectedWorkstream.request_capability_id ? (
        <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 'none' }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between">
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 750 }}>
                    Selected workstream
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 850, wordBreak: 'break-word' }}>
                    {selectedWorkstream.capability_name || 'Requested Capability'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {directBusinessUnitLabel(selectedWorkstream.provider_business_unit)}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="flex-start">
                  <Chip size="small" variant="outlined" label={`${formatDirectLabel(fulfillmentMode)} fulfillment`} />
                  <Chip size="small" variant="outlined" label={`${formatDirectLabel(selectedWorkstream.acceptance_mode || snapshot.acceptance_mode || 'manual')} acceptance`} />
                  <Chip size="small" color={directStatusColor(selectedWorkstream.status)} label={formatDirectLabel(selectedWorkstream.status)} />
                </Stack>
              </Stack>
              {automatedWorkstreamWithoutExecution(selectedWorkstream) ? (
                <Alert severity="warning">
                  Automated fulfillment is accepted, but no Run has started. Review asset selection and retry execution if needed.
                </Alert>
              ) : null}
              {automatedFulfillment && selectedWorkstream.status === 'awaiting_resource' ? (
                <Alert severity="warning">
                  AMX could not find an eligible Asset. Review the asset diagnostics and resolve fulfillment before retrying.
                </Alert>
              ) : null}
              {!automatedFulfillment ? (
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent="space-between"
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary">
                    Responsible Person
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PersonRoundedIcon fontSize="small" color="primary" />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 800, wordBreak: 'break-word' }}>
                        {directUserLabel(selectedWorkstream.responsible_user)}
                      </Typography>
                      {selectedWorkstream.assignment?.lead?.assigned_at ? (
                        <Typography variant="caption" color="text.secondary">
                          Claimed {formatDate(selectedWorkstream.assignment.lead.assigned_at)}
                        </Typography>
                      ) : null}
                    </Box>
                  </Stack>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                  {selectedWorkstream.authorization?.can_claim ? (
                    <Button
                      variant="contained"
                      startIcon={<PersonRoundedIcon />}
                      onClick={() => postWorkAction(selectedWorkstream, 'claim')}
                      disabled={workActionBusy === `${selectedWorkstream.request_capability_id}:claim`}
                    >
                      {workActionBusy === `${selectedWorkstream.request_capability_id}:claim` ? 'Claiming...' : 'Claim Work'}
                    </Button>
                  ) : null}
                  {selectedWorkstream.authorization?.can_release ? (
                    <Button
                      variant="outlined"
                      onClick={() => postWorkAction(selectedWorkstream, 'release')}
                      disabled={workActionBusy === `${selectedWorkstream.request_capability_id}:release`}
                    >
                      {workActionBusy === `${selectedWorkstream.request_capability_id}:release` ? 'Releasing...' : 'Release Work'}
                    </Button>
                  ) : null}
                  {selectedWorkstream.authorization?.can_start ? (
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<PlayArrowRoundedIcon />}
                      onClick={() => postWorkAction(selectedWorkstream, 'start')}
                      disabled={workActionBusy === `${selectedWorkstream.request_capability_id}:start`}
                    >
                      {workActionBusy === `${selectedWorkstream.request_capability_id}:start` ? 'Starting...' : 'Start Work'}
                    </Button>
                  ) : null}
                  {selectedWorkstream.authorization?.can_wait ? (
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<PauseCircleRoundedIcon />}
                      onClick={() => setWaitDialog({
                        open: true,
                        workstreamId: selectedWorkstream.request_capability_id,
                        waitingOn: 'requester',
                        reason: '',
                      })}
                    >
                      Mark Waiting
                    </Button>
                  ) : null}
                  {selectedWorkstream.authorization?.can_resume ? (
                    <Button
                      variant="contained"
                      startIcon={<RestartAltRoundedIcon />}
                      onClick={() => postWorkAction(selectedWorkstream, 'resume')}
                      disabled={workActionBusy === `${selectedWorkstream.request_capability_id}:resume`}
                    >
                      {workActionBusy === `${selectedWorkstream.request_capability_id}:resume` ? 'Resuming...' : 'Resume Work'}
                    </Button>
                  ) : null}
                  {selectedWorkstream.authorization?.can_fail ? (
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<ErrorOutlineRoundedIcon />}
                      onClick={() => setFailDialog({
                        open: true,
                        workstreamId: selectedWorkstream.request_capability_id,
                        reason: '',
                      })}
                    >
                      Mark Failed
                    </Button>
                  ) : null}
                  {selectedWorkstream.authorization?.can_complete ? (
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircleRoundedIcon />}
                      onClick={() => postWorkAction(selectedWorkstream, 'complete')}
                      disabled={workActionBusy === `${selectedWorkstream.request_capability_id}:complete`}
                    >
                      {workActionBusy === `${selectedWorkstream.request_capability_id}:complete` ? 'Completing...' : 'Complete Work'}
                    </Button>
                  ) : null}
                </Stack>
              </Stack>
              ) : null}
              <CapabilityFulfillmentPanel
                workstream={selectedWorkstream}
                onRefresh={refreshDirectWorkspace}
              />
              {selectedWorkstream.status === 'waiting' && selectedWorkstream.waiting_reason ? (
                <Alert severity="warning">
                  Waiting on {formatDirectLabel(selectedWorkstream.waiting_on)}: {selectedWorkstream.waiting_reason}
                </Alert>
              ) : null}
              {selectedWorkstream.status === 'failed' && selectedWorkstream.failure_reason ? (
                <Alert severity="error">
                  {selectedWorkstream.failure_reason}
                </Alert>
              ) : null}
              {selectedWorkstream.provider_response_message ? (
                <Alert severity={selectedWorkstream.status === 'declined' ? 'warning' : 'info'}>
                  {selectedWorkstream.provider_response_message}
                </Alert>
              ) : null}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 850, mb: 1 }}>Latest and active runs</Typography>
                <Stack spacing={1}>
                  {(selectedWorkstream.runs || []).length ? selectedWorkstream.runs.map((run) => (
                    <AssetRunCard key={run.run_id} run={run} compact />
                  )) : (
                    <DirectEmptyState
                      title="No asset runs yet"
                      description="Automated execution and provider-linked runs will appear here."
                    />
                  )}
                </Stack>
              </Box>
              <DirectValueRows
                title="Shared Need Information"
                fields={snapshot.shared_fields || []}
                values={selectedWorkstream.shared_request_values}
                artifacts={selectedWorkstream.artifacts || []}
                source="shared"
                onDownload={handleDownloadArtifact}
                downloadingArtifact={downloadingArtifact}
              />
              <DirectValueRows
                title="Provider-Specific Information"
                fields={snapshot.additional_fields || []}
                values={selectedWorkstream.capability_request_values}
                artifacts={selectedWorkstream.artifacts || []}
                source="capability"
                onDownload={handleDownloadArtifact}
                downloadingArtifact={downloadingArtifact}
              />
              <DirectOutputs
                workstream={selectedWorkstream}
                onProvideOutput={handleProvideOutput}
                onDownloadOutput={handleDownloadOutput}
                busyKey={workActionBusy}
              />
              {!automatedFulfillment ? <WorkstreamTasks
                workstream={selectedWorkstream}
                onRefresh={refreshDirectWorkspace}
                initialTaskId={
                  selectedWorkstream.request_capability_id === initialWorkstreamId
                    ? initialTaskId
                    : ''
                }
              /> : null}
            </Stack>
          </CardContent>
        </Card>
        ) : null}
      </Stack>
    );
  };

  const renderArtifactRow = (artifact, subtitle, key, onDownload = handleDownloadArtifact) => (
    <Card
      key={key}
      variant="outlined"
      sx={{
        borderRadius: 2,
        boxShadow: 'none',
        transition: (theme) => theme.transitions.create(['border-color', 'box-shadow']),
        '&:hover': { borderColor: 'primary.light', boxShadow: 1 },
      }}
    >
      <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
          <Stack direction="row" spacing={1.25} sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                color: 'primary.main',
                display: 'grid',
                placeItems: 'center',
                flex: '0 0 auto',
              }}
            >
              <InsertDriveFileRoundedIcon fontSize="small" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 800, wordBreak: 'break-word' }}>
                {artifact.filename || artifact.file_name || artifact.file_id || artifact.artifact_id}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">{subtitle}</Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {artifact.created_at || artifact.provided_at
                  ? `Added ${formatDate(artifact.created_at || artifact.provided_at)}`
                  : 'Timestamp unavailable'}
              </Typography>
            </Box>
          </Stack>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadRoundedIcon />}
            onClick={() => onDownload(artifact)}
            disabled={downloadingArtifact === artifact.file_id}
          >
            {downloadingArtifact === artifact.file_id ? 'Downloading...' : 'Download'}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );

  const renderArtifacts = () => {
    const outputArtifacts = workstreams.flatMap((workstream) => (
      (workstream.expected_outputs || [])
        .filter((output) => output.fulfillment?.artifact_id)
        .map((output) => ({
          ...output.fulfillment,
          request_capability_id: workstream.request_capability_id,
          capability_name: workstream.capability_name,
          output_name: output.name || formatDirectLabel(directOutputKey(output)),
          output_key: directOutputKey(output),
        }))
    ));
    const canUploadArtifact = Boolean(selectedWorkstream.authorization?.can_upload_artifact);
    const workspaceFiles = [
      ...generalArtifacts.filter((artifact) => artifact.field_key === 'collaboration'),
      ...artifacts.filter((artifact) => artifact.field_key === 'collaboration'),
    ];
    const inputGeneralArtifacts = generalArtifacts.filter((artifact) => artifact.field_key !== 'collaboration');
    const inputWorkstreamArtifacts = artifacts.filter((artifact) => artifact.field_key !== 'collaboration');

    return (
      <Stack spacing={2}>
        {canUploadArtifact ? (
          <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 'none' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>Add a workspace file</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Choose whether the file is shared with the selected workstream or the whole request.
                  </Typography>
                </Box>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
                  <Button variant="outlined" component="label" startIcon={<UploadFileRoundedIcon />}>
                    {artifactUpload.file ? artifactUpload.file.name : 'Choose Artifact'}
                    <input
                      hidden
                      type="file"
                      onChange={(event) => setArtifactUpload((current) => ({
                        ...current,
                        file: event.target.files?.[0] || null,
                      }))}
                    />
                  </Button>
                  <ToggleButtonGroup
                    value={artifactUpload.scope}
                    exclusive
                    size="small"
                    onChange={(_event, value) => {
                      if (value) {
                        setArtifactUpload((current) => ({ ...current, scope: value }));
                      }
                    }}
                  >
                    <ToggleButton value="request_capability">Workstream</ToggleButton>
                    <ToggleButton value="request">Request</ToggleButton>
                  </ToggleButtonGroup>
                  <Button
                    variant="contained"
                    onClick={handleArtifactUpload}
                    disabled={workActionBusy === `artifact:${selectedWorkstream.request_capability_id}`}
                  >
                    {workActionBusy === `artifact:${selectedWorkstream.request_capability_id}` ? 'Uploading...' : 'Upload'}
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ) : null}

        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 850 }}>Inputs</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Request inputs shared with one or more provider workstreams.
          </Typography>
          <Stack spacing={1.25}>
            {inputGeneralArtifacts.map((artifact) => renderArtifactRow(
              { ...artifact, request_capability_id: selectedWorkstream.request_capability_id },
              `Request input · ${formatDirectLabel(artifact.field_key)}`,
              `general:${artifact.file_id}`,
            ))}
            {inputWorkstreamArtifacts.map((artifact) => renderArtifactRow(
              artifact,
              `${artifact.capability_name || 'Requested Capability'} · ${formatDirectLabel(artifact.source)} input · ${formatDirectLabel(artifact.field_key)}`,
              `${artifact.request_capability_id}:${artifact.file_id}`,
            ))}
            {!inputGeneralArtifacts.length && !inputWorkstreamArtifacts.length ? (
              <DirectEmptyState
                title="No input artifacts attached"
                description="Files submitted with the request or shared with a workstream will appear here."
              />
            ) : null}
          </Stack>
        </Box>

        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 850 }}>Outputs</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Deliverables produced by provider workstreams or asset runs.
          </Typography>
          <Stack spacing={1.25}>
            {outputArtifacts.map((artifact) => renderArtifactRow(
              artifact,
              `${artifact.capability_name || 'Requested Capability'} · ${artifact.output_name}${artifact.run_ids?.length ? ` · Run ${String(artifact.run_ids[0]).slice(0, 8)}` : ''}`,
              `output:${artifact.request_capability_id}:${artifact.output_key}`,
              (outputArtifact) => handleDownloadOutput({
                key: outputArtifact.output_key,
                name: outputArtifact.output_name,
                fulfillment: outputArtifact,
              }),
            ))}
            {!outputArtifacts.length ? (
              <DirectEmptyState
                title="No output artifacts have been provided yet"
                description="Outputs generated by provider workstreams or asset runs will appear here."
              />
            ) : null}
          </Stack>
        </Box>

        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 850 }}>Workspace files</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Files added manually during collaboration.
          </Typography>
          <Stack spacing={1.25}>
            {workspaceFiles.map((artifact) => renderArtifactRow(
              {
                ...artifact,
                request_capability_id: artifact.request_capability_id || selectedWorkstream.request_capability_id,
              },
              `${artifact.capability_name || 'General workspace'} · Manual upload`,
              `workspace:${artifact.request_capability_id || 'general'}:${artifact.file_id}`,
            ))}
            {!workspaceFiles.length ? (
              <DirectEmptyState
                title="No workspace files"
                description="Files uploaded during collaboration will appear here."
              />
            ) : null}
          </Stack>
        </Box>
      </Stack>
    );
  };

  const renderMessages = () => {
    const requestingBusinessUnitId = workspaceData?.requesting_business_unit?.business_unit_id;
    const getSenderRole = (message) => {
      const explicitRole = message.metadata?.sender_role || message.sender_role;
      if (explicitRole) return formatDirectLabel(explicitRole);
      if (message.sender_business_unit_id === requestingBusinessUnitId) return 'Requestor';
      if (workstreams.some((workstream) => (
        workstream.provider_business_unit?.business_unit_id === message.sender_business_unit_id
      ))) return 'Provider';
      if (message.message_type === 'provider_response') return 'Provider';
      return message.sender ? 'Participant' : 'System';
    };
    const normalizedMessages = [
      ...messages.map((message) => ({
        id: message.message_id || message.request_capability_message_id,
        workspaceId: message.workspace_id || workspace.workspace_id,
        workstreamId: message.workstream_id || message.request_capability_id || null,
        senderName: message.sender ? directUserLabel(message.sender) : (
          message.message_type === 'provider_response' ? 'Provider team' : 'System'
        ),
        senderOrg: message.business_unit ? directBusinessUnitLabel(message.business_unit) : '',
        senderRole: getSenderRole(message),
        body: message.body,
        createdAt: message.created_at,
        attachments: message.attachments || message.metadata?.attachments || [],
        messageType: message.message_type === 'system' ? 'system' : 'user',
      })),
      ...activity.filter((event) => !String(event.type || '').endsWith('.message_created')).map((event) => ({
        id: `activity-message-${event.activity_id}`,
        workspaceId: workspace.workspace_id,
        workstreamId: event.request_capability_id || null,
        senderName: 'System',
        senderOrg: '',
        senderRole: 'System',
        body: event.label,
        createdAt: event.timestamp,
        attachments: [],
        messageType: 'system',
      })),
    ].filter((message) => message.id && message.createdAt)
      .sort((left, right) => new Date(left.createdAt) - new Date(right.createdAt));
    const channelMessages = normalizedMessages.filter((message) => (
      selectedMessageChannel.type === 'workspace'
        ? !message.workstreamId
        : message.workstreamId === selectedMessageChannel.workstreamId
    ));
    const selectedContextWorkstream = selectedMessageWorkstream || selectedWorkstream;
    const canSendMessage = selectedMessageChannel.type === 'workspace'
      ? workstreams.some((workstream) => workstream.authorization?.can_send_message)
      : Boolean(selectedMessageWorkstream?.authorization?.can_send_message);
    const selectedLatestRun = directLatestRun(selectedContextWorkstream);
    const messageBusyKey = `message:${selectedMessageChannel.id}`;

    const lastChannelMessage = (channel) => normalizedMessages.filter((message) => (
      channel.type === 'workspace'
        ? !message.workstreamId
        : message.workstreamId === channel.workstreamId
    )).slice(-1)[0];

    return (
      <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 'none', overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: '220px minmax(0, 1fr)',
              lg: '230px minmax(360px, 1fr) 270px',
            },
            gridTemplateAreas: {
              xs: '"channels" "thread" "context"',
              md: '"channels thread" "context context"',
              lg: '"channels thread context"',
            },
            minHeight: { lg: 620 },
          }}
        >
          <Box
            sx={{
              gridArea: 'channels',
              p: 1.5,
              bgcolor: (theme) => alpha(theme.palette.action.hover, 0.32),
              borderRight: { md: (theme) => `1px solid ${theme.palette.divider}` },
              borderBottom: { xs: (theme) => `1px solid ${theme.palette.divider}`, lg: 0 },
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 850, px: 0.75 }}>Channels</Typography>
            <Typography variant="overline" color="text.secondary" sx={{ px: 0.75, mt: 1.25, display: 'block', lineHeight: 1.8 }}>
              Workspace
            </Typography>
            {messageChannels.filter((channel) => channel.type === 'workspace').map((channel) => {
              const selected = selectedMessageChannel.id === channel.id;
              const lastMessage = lastChannelMessage(channel);
              return (
                <ButtonBase
                  key={channel.id}
                  onClick={() => setSelectedMessageChannelId(channel.id)}
                  sx={{
                    width: '100%',
                    p: 1,
                    borderRadius: 1.5,
                    textAlign: 'left',
                    justifyContent: 'flex-start',
                    bgcolor: selected ? 'background.paper' : 'transparent',
                    border: (theme) => `1px solid ${selected ? theme.palette.divider : 'transparent'}`,
                  }}
                >
                  <Box sx={{ minWidth: 0, width: '100%' }}>
                    <Typography variant="body2" sx={{ fontWeight: selected ? 850 : 700 }}># General</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {lastMessage ? directTimeLabel(lastMessage.createdAt) : 'Workspace-wide'}
                    </Typography>
                  </Box>
                </ButtonBase>
              );
            })}
            <Typography variant="overline" color="text.secondary" sx={{ px: 0.75, mt: 1.5, display: 'block', lineHeight: 1.8 }}>
              Workstreams
            </Typography>
            <Stack spacing={0.5}>
              {messageChannels.filter((channel) => channel.type === 'workstream').map((channel) => {
                const selected = selectedMessageChannel.id === channel.id;
                const lastMessage = lastChannelMessage(channel);
                return (
                  <ButtonBase
                    key={channel.id}
                    onClick={() => {
                      setSelectedMessageChannelId(channel.id);
                      setSelectedWorkstreamId(channel.workstreamId);
                    }}
                    sx={{
                      width: '100%',
                      p: 1,
                      borderRadius: 1.5,
                      textAlign: 'left',
                      justifyContent: 'flex-start',
                      bgcolor: selected ? 'background.paper' : 'transparent',
                      border: (theme) => `1px solid ${selected ? theme.palette.divider : 'transparent'}`,
                    }}
                  >
                    <Box sx={{ minWidth: 0, width: '100%' }}>
                      <Typography variant="body2" sx={{ fontWeight: selected ? 850 : 700, wordBreak: 'break-word' }}>
                        # {channel.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.35 }}>
                        {channel.providerName}
                      </Typography>
                      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="space-between" sx={{ mt: 0.6 }}>
                        <Chip
                          size="small"
                          color={directStatusColor(channel.status)}
                          label={formatDirectLabel(channel.status)}
                          sx={{ height: 21, '& .MuiChip-label': { px: 0.75, fontSize: '0.66rem' } }}
                        />
                        {lastMessage ? <Typography variant="caption" color="text.secondary">{directTimeLabel(lastMessage.createdAt)}</Typography> : null}
                      </Stack>
                    </Box>
                  </ButtonBase>
                );
              })}
            </Stack>
          </Box>

          <Box sx={{ gridArea: 'thread', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 850 }}>{selectedMessageChannel.label}</Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedMessageChannel.type === 'workspace'
                  ? 'Visible to all workspace participants'
                  : `${selectedMessageChannel.providerName} workstream`}
              </Typography>
            </Box>
            <Stack spacing={1.25} sx={{ p: 2, flex: 1, minHeight: 310, maxHeight: 470, overflowY: 'auto' }}>
              {channelMessages.map((message) => message.messageType === 'system' ? (
                <Box
                  key={message.id}
                  sx={{
                    py: 0.75,
                    px: 1.25,
                    borderLeft: (theme) => `2px solid ${theme.palette.divider}`,
                    bgcolor: (theme) => alpha(theme.palette.action.hover, 0.35),
                    borderRadius: '0 8px 8px 0',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 750 }}>System</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {message.body} · {formatDate(message.createdAt)}
                  </Typography>
                </Box>
              ) : (
                <Box key={message.id} sx={{ py: 0.5 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.5} justifyContent="space-between" alignItems={{ sm: 'flex-start' }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 850 }}>{message.senderName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[message.senderOrg, message.senderRole].filter(Boolean).join(' · ')}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      {formatDate(message.createdAt)}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.8, lineHeight: 1.65 }}>
                    {message.body}
                  </Typography>
                  {message.attachments?.length ? (
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                      {message.attachments.map((attachment, index) => (
                        <Chip
                          key={attachment.id || attachment.name || index}
                          size="small"
                          icon={<AttachFileRoundedIcon />}
                          label={attachment.name || attachment.filename || 'Attachment'}
                          component={attachment.url ? 'a' : 'div'}
                          href={attachment.url || undefined}
                          clickable={Boolean(attachment.url)}
                        />
                      ))}
                    </Stack>
                  ) : null}
                </Box>
              ))}
              {!channelMessages.length ? (
                <DirectEmptyState
                  title={`No messages in ${selectedMessageChannel.label}`}
                  description={selectedMessageChannel.type === 'workspace'
                    ? 'Use this channel for updates relevant to everyone in the workspace.'
                    : 'Workstream-specific decisions and provider updates will appear here.'}
                />
              ) : null}
            </Stack>
            <Box sx={{ p: 1.5, borderTop: (theme) => `1px solid ${theme.palette.divider}`, bgcolor: 'background.paper' }}>
              <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary">
                  Sending to: <Box component="span" sx={{ color: 'text.primary', fontWeight: 850 }}>{selectedMessageChannel.label}</Box>
                </Typography>
                <TextField
                  placeholder="Write a message..."
                  value={messageDraft}
                  onChange={(event) => setMessageDraft(event.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                  disabled={!canSendMessage}
                  onKeyDown={(event) => {
                    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter' && canSendMessage && messageDraft.trim()) {
                      event.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />
                <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    {canSendMessage ? 'Ctrl/⌘ + Enter to send' : 'You have read-only access to this channel'}
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SendRoundedIcon />}
                    onClick={handleSendMessage}
                    disabled={!canSendMessage || !messageDraft.trim() || workActionBusy === messageBusyKey}
                  >
                    {workActionBusy === messageBusyKey ? 'Sending...' : 'Send'}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Box>

          <Box
            sx={{
              gridArea: 'context',
              p: 2,
              bgcolor: (theme) => alpha(theme.palette.action.hover, 0.2),
              borderLeft: { lg: (theme) => `1px solid ${theme.palette.divider}` },
              borderTop: { xs: (theme) => `1px solid ${theme.palette.divider}`, lg: 0 },
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 850, mb: 1.5 }}>Channel context</Typography>
            {selectedMessageChannel.type === 'workspace' ? (
              <Stack spacing={1.35}>
                {[
                  ['Requestor', directBusinessUnitLabel(workspaceData?.requesting_business_unit)],
                  ['Workspace status', formatDirectLabel(requestStatus)],
                  ['Providers involved', String(workstreams.length)],
                  ['Artifacts', String(artifactCount)],
                  ['Runs', String(allRuns.length)],
                ].map(([label, value]) => (
                  <Box key={label}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 750 }}>{value}</Typography>
                  </Box>
                ))}
                <Divider />
                <Box>
                  <Typography variant="caption" color="text.secondary">Provider teams</Typography>
                  <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                    {workstreams.map((workstream) => (
                      <Typography key={workstream.request_capability_id} variant="body2" sx={{ fontWeight: 700 }}>
                        {directBusinessUnitLabel(workstream.provider_business_unit)}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              </Stack>
            ) : (
              <Stack spacing={1.35}>
                {[
                  ['Capability', selectedContextWorkstream.capability_name || 'Requested Capability'],
                  ['Provider', directBusinessUnitLabel(selectedContextWorkstream.provider_business_unit)],
                  ['Status', formatDirectLabel(selectedContextWorkstream.status)],
                  ['Fulfillment', formatDirectLabel(selectedContextWorkstream.fulfillment_mode || selectedContextWorkstream.capability_snapshot?.fulfillment_mode || 'manual')],
                  ['Acceptance', formatDirectLabel(selectedContextWorkstream.acceptance_mode || selectedContextWorkstream.capability_snapshot?.acceptance_mode || 'manual')],
                  ['Artifacts', String((selectedContextWorkstream.artifacts || []).length)],
                ].map(([label, value]) => (
                  <Box key={label}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 750, wordBreak: 'break-word' }}>{value}</Typography>
                  </Box>
                ))}
                <Divider />
                <Box>
                  <Typography variant="caption" color="text.secondary">Latest run</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 750 }}>
                    {selectedLatestRun ? directRunLabel(selectedLatestRun) : 'No runs yet'}
                  </Typography>
                  {selectedLatestRun ? (
                    <Typography variant="caption" color="text.secondary">
                      {formatDirectLabel(selectedLatestRun.status)} · Started {formatDate(selectedLatestRun.started_at)}
                    </Typography>
                  ) : null}
                </Box>
                <Button size="small" variant="outlined" fullWidth onClick={() => openWorkstream(selectedContextWorkstream)}>
                  Open workstream
                </Button>
              </Stack>
            )}
          </Box>
        </Box>
      </Card>
    );
  };

  const renderActivity = () => {
    const groups = activity.reduce((result, event) => {
      const day = directDayLabel(event.timestamp);
      if (!result.has(day)) result.set(day, []);
      result.get(day).push(event);
      return result;
    }, new Map());
    return (
      <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 'none' }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 850 }}>Workspace timeline</Typography>
              <Typography variant="body2" color="text.secondary">
                A read-only record of request, workstream, run, artifact, and collaboration events.
              </Typography>
            </Box>
            {Array.from(groups.entries()).map(([day, events]) => (
              <Box key={day}>
                <Typography variant="subtitle2" sx={{ fontWeight: 850, mb: 1.25 }}>{day}</Typography>
                <Stack>
                  {events.map((event, index) => {
                    const relatedWorkstream = workstreams.find((workstream) => (
                      workstream.request_capability_id === event.request_capability_id
                    ));
                    const actor = event.business_unit
                      ? directBusinessUnitLabel(event.business_unit)
                      : (event.actor_email || (
                        String(event.type || '').includes('asset') || String(event.type || '').includes('run') ? 'Asset' : 'System'
                      ));
                    return (
                      <Stack key={event.activity_id} direction="row" spacing={1.5}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.65 }}>
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              bgcolor: index === 0 ? 'primary.main' : 'background.paper',
                              border: (theme) => `2px solid ${theme.palette.primary.main}`,
                            }}
                          />
                          {index < events.length - 1 ? <Box sx={{ width: 1, flex: 1, minHeight: 34, bgcolor: 'divider' }} /> : null}
                        </Box>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={{ xs: 0.25, sm: 1.5 }}
                          justifyContent="space-between"
                          sx={{ minWidth: 0, width: '100%', pb: index < events.length - 1 ? 1.5 : 0 }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 800 }}>{event.label}</Typography>
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
                              <Typography variant="caption" color="text.secondary">{actor}</Typography>
                              {relatedWorkstream ? (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={relatedWorkstream.capability_name || 'Workstream'}
                                  sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: '0.66rem' } }}
                                />
                              ) : null}
                            </Stack>
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                            {directTimeLabel(event.timestamp)}
                          </Typography>
                        </Stack>
                      </Stack>
                    );
                  })}
                </Stack>
              </Box>
            ))}
            {!activity.length ? (
              <DirectEmptyState title="No activity yet" description="Operational events will appear here as the workspace progresses." />
            ) : null}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Stack spacing={2}>
      <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 'none', overflow: 'hidden' }}>
        <Box sx={{ p: { xs: 2, md: 2.25 } }}>
          <Stack spacing={1.25}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ md: 'flex-start' }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                  Direct Request Workspace
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 850, wordBreak: 'break-word', mt: 0.2 }}>
                  {request.title || workspace.title || 'Workspace'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                  {directBusinessUnitLabel(workspaceData?.requesting_business_unit)} → Provider Workstreams
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', pt: { md: 0.4 } }}>
                Last updated {formatDate(lastUpdated || workspace.updated_at)}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              <Chip size="small" icon={<HubRoundedIcon />} label={formatDirectLabel(requestStatus)} color={directStatusColor(requestStatus)} />
              <Chip size="small" icon={<ShieldMoonRoundedIcon />} label={workspace.is_cui ? 'CUI' : 'Unclassified'} variant="outlined" />
              <Chip size="small" icon={<WorkRoundedIcon />} label={`${workstreams.length} Workstream${workstreams.length === 1 ? '' : 's'}`} variant="outlined" />
              <Chip size="small" icon={<AttachFileRoundedIcon />} label={`${artifactCount} Artifact${artifactCount === 1 ? '' : 's'}`} variant="outlined" />
              <Chip size="small" icon={<PlayArrowRoundedIcon />} label={`${activeRunCount} Active Run${activeRunCount === 1 ? '' : 's'}`} variant="outlined" />
              {attentionCount ? <Chip size="small" color="warning" label={`${attentionCount} Need${attentionCount === 1 ? 's' : ''} attention`} /> : null}
            </Stack>
          </Stack>
        </Box>
        <Divider />
        <Tabs
          value={tab}
          onChange={(_event, value) => setTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: 44, px: 1, '& .MuiTab-root': { minHeight: 44, py: 1, textTransform: 'none', fontWeight: 750 } }}
        >
          <Tab value="overview" label="Overview" />
          <Tab value="work" label="Work" />
          <Tab value="artifacts" label="Artifacts" />
          <Tab value="messages" label="Messages" />
          <Tab value="activity" label="Activity" />
        </Tabs>
      </Card>

      {downloadError ? <Alert severity="error">{downloadError}</Alert> : null}
      {workActionError ? <Alert severity="error">{workActionError}</Alert> : null}
      {workActionSuccess ? <Alert severity="success">{workActionSuccess}</Alert> : null}

      {tab === 'overview' ? renderOverview() : null}
      {tab === 'work' ? renderWork() : null}
      {tab === 'artifacts' ? renderArtifacts() : null}
      {tab === 'messages' ? renderMessages() : null}
      {tab === 'activity' ? renderActivity() : null}

      <Dialog
        open={waitDialog.open}
        onClose={() => setWaitDialog({ open: false, workstreamId: '', waitingOn: 'requester', reason: '' })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Mark Waiting</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Waiting On"
              value={waitDialog.waitingOn}
              onChange={(event) => setWaitDialog((current) => ({ ...current, waitingOn: event.target.value }))}
              fullWidth
            >
              <MenuItem value="requester">Requester</MenuItem>
              <MenuItem value="provider">Provider</MenuItem>
              <MenuItem value="external">External</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>
            <TextField
              label="Reason"
              value={waitDialog.reason}
              onChange={(event) => setWaitDialog((current) => ({ ...current, reason: event.target.value }))}
              fullWidth
              multiline
              minRows={3}
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWaitDialog({ open: false, workstreamId: '', waitingOn: 'requester', reason: '' })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleWaitSubmit}
            disabled={!waitDialog.reason.trim() || Boolean(workActionBusy)}
          >
            Mark Waiting
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={failDialog.open}
        onClose={() => setFailDialog({ open: false, workstreamId: '', reason: '' })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Mark Failed</DialogTitle>
        <DialogContent>
          <TextField
            label="Failure Reason"
            value={failDialog.reason}
            onChange={(event) => setFailDialog((current) => ({ ...current, reason: event.target.value }))}
            fullWidth
            multiline
            minRows={4}
            required
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFailDialog({ open: false, workstreamId: '', reason: '' })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleFailSubmit}
            disabled={!failDialog.reason.trim() || Boolean(workActionBusy)}
          >
            Mark Failed
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={outputDialog.open}
        onClose={() => setOutputDialog({ open: false, workstreamId: '', output: null, file: null, description: '', runIds: [] })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Provide {outputDialog.output?.name || formatDirectLabel(directOutputKey(outputDialog.output || {}))}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {String(outputDialog.output?.kind || 'file').toLowerCase() === 'asset_run' ? (
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  Select one or more Runs already associated with this Capability Request.
                </Typography>
                {(selectedWorkstream.runs || []).length ? selectedWorkstream.runs.map((run) => (
                  <Box key={run.run_id} sx={{ border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 1.5, p: 1 }}>
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={outputDialog.runIds.includes(run.run_id)}
                          onChange={() => setOutputDialog((current) => ({
                            ...current,
                            runIds: current.runIds.includes(run.run_id)
                              ? current.runIds.filter((runId) => runId !== run.run_id)
                              : [...current.runIds, run.run_id],
                          }))}
                        />
                      )}
                      label={`${run.asset?.asset_name || 'Asset'} · ${String(run.run_id).slice(0, 8)} · ${String(run.status || '').toUpperCase()}`}
                    />
                  </Box>
                )) : <Alert severity="info">No associated Runs are available to select.</Alert>}
              </Stack>
            ) : (
              <Button variant="outlined" component="label" startIcon={<UploadFileRoundedIcon />}>
                {outputDialog.file ? outputDialog.file.name : 'Choose File'}
                <input
                  hidden
                  type="file"
                  onChange={(event) => setOutputDialog((current) => ({
                    ...current,
                    file: event.target.files?.[0] || null,
                  }))}
                />
              </Button>
            )}
            <TextField
              label="Description"
              value={outputDialog.description}
              onChange={(event) => setOutputDialog((current) => ({ ...current, description: event.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOutputDialog({ open: false, workstreamId: '', output: null, file: null, description: '', runIds: [] })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleOutputSubmit}
            disabled={(
              String(outputDialog.output?.kind || 'file').toLowerCase() === 'asset_run'
                ? !outputDialog.runIds.length
                : !outputDialog.file
            ) || Boolean(workActionBusy)}
          >
            Provide Output
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function SharingDialog({
  open,
  context,
  form,
  onFormChange,
  onClose,
  onSubmit,
  busy,
}) {
  const resourceType = context?.resourceType || 'workspace';
  const fields = context?.schemaFields || [];
  const files = context?.files || [];
  const threads = context?.threads || [];
  const isWorkspace = resourceType === 'workspace';
  const isCard = resourceType === 'exchange_card';
  const [targetOptions, setTargetOptions] = useState([]);
  const [targetsLoading, setTargetsLoading] = useState(false);

  useEffect(() => {
    if (!open || form.targetMode !== 'internal') {
      setTargetOptions([]);
      setTargetsLoading(false);
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setTargetsLoading(true);
      try {
        const response = await axiosInstance.get('/sharing/targets', {
          params: { query: form.targetSearch || '' },
        });
        if (!cancelled) {
          setTargetOptions(Array.isArray(response.data?.users) ? response.data.users : []);
        }
      } catch (err) {
        console.error('Failed to load internal share targets', err);
        if (!cancelled) {
          setTargetOptions([]);
        }
      } finally {
        if (!cancelled) {
          setTargetsLoading(false);
        }
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, form.targetMode, form.targetSearch]);

  const toggleListValue = (key, value) => {
    onFormChange((current) => {
      const values = new Set(current[key] || []);
      if (values.has(value)) {
        values.delete(value);
      } else {
        values.add(value);
      }
      return { ...current, [key]: [...values] };
    });
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Share {context?.title || 'resource'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {isWorkspace ? (
            <Alert severity="warning">
              Sharing a workspace can expose every thread and Exchange Card that is not excluded.
            </Alert>
          ) : null}

          <ToggleButtonGroup
            value={form.targetMode}
            exclusive
            size="small"
            onChange={(_event, nextMode) => {
              if (!nextMode) {
                return;
              }
              onFormChange((current) => ({
                ...current,
                targetMode: nextMode,
                targetUser: null,
                targetSearch: '',
                targetEmail: '',
              }));
            }}
            sx={{ alignSelf: 'flex-start' }}
          >
            <ToggleButton value="internal">Internal</ToggleButton>
            <ToggleButton value="external">External</ToggleButton>
          </ToggleButtonGroup>

          {form.targetMode === 'internal' ? (
            <Autocomplete
              options={targetOptions}
              value={form.targetUser}
              inputValue={form.targetSearch}
              loading={targetsLoading}
              filterOptions={(options) => options}
              getOptionLabel={shareTargetLabel}
              isOptionEqualToValue={(option, value) => (
                option.user_id === value.user_id && option.partner_id === value.partner_id
              )}
              onInputChange={(_event, value) => onFormChange((current) => ({ ...current, targetSearch: value }))}
              onChange={(_event, value) => onFormChange((current) => ({
                ...current,
                targetUser: value,
                targetEmail: '',
              }))}
              noOptionsText={form.targetSearch ? 'No internal users found' : 'Start typing a name or email'}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Internal user"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {targetsLoading ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          ) : (
            <TextField
              label="External AMX user email"
              type="email"
              value={form.targetEmail}
              onChange={(event) => onFormChange((current) => ({ ...current, targetEmail: event.target.value }))}
              fullWidth
            />
          )}

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <TextField
              select
              label="Permission"
              value={form.permission}
              onChange={(event) => onFormChange((current) => ({ ...current, permission: event.target.value }))}
              fullWidth
            >
              <MenuItem value="view">View</MenuItem>
              <MenuItem value="comment">Comment</MenuItem>
              <MenuItem value="edit">Edit</MenuItem>
              {isCard ? <MenuItem value="owner_transfer">Transfer Ownership</MenuItem> : null}
            </TextField>
            <TextField
              label="Expires at"
              type="datetime-local"
              value={form.expiresAt}
              onChange={(event) => onFormChange((current) => ({ ...current, expiresAt: event.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>

          {isWorkspace && threads.length ? (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                Exclude Threads
              </Typography>
              <Stack spacing={0.25}>
                {threads.map((thread) => (
                  <FormControlLabel
                    key={thread.corr_id}
                    control={
                      <Checkbox
                        checked={form.excludedCorrIds.includes(thread.corr_id)}
                        onChange={() => toggleListValue('excludedCorrIds', thread.corr_id)}
                      />
                    }
                    label={thread.thread_title || thread.corr_id}
                  />
                ))}
              </Stack>
            </Box>
          ) : null}

          {isCard && fields.length ? (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                Redact Fields
              </Typography>
              <Stack spacing={0.25}>
                {fields.map((field) => {
                  const name = getSchemaFieldName(field);
                  return (
                    <FormControlLabel
                      key={name}
                      control={
                        <Checkbox
                          checked={form.redactedFields.includes(name)}
                          onChange={() => toggleListValue('redactedFields', name)}
                        />
                      }
                      label={getSchemaFieldLabel(field)}
                    />
                  );
                })}
              </Stack>
            </Box>
          ) : null}

          {isCard && files.length ? (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                Hide Files
              </Typography>
              <Stack spacing={0.25}>
                {files.map((file) => {
                  const fileId = String(file.file_id || file.filename || file.name || '');
                  return (
                    <FormControlLabel
                      key={fileId}
                      control={
                        <Checkbox
                          checked={form.redactedFileIds.includes(fileId)}
                          onChange={() => toggleListValue('redactedFileIds', fileId)}
                        />
                      }
                      label={file.filename || file.name || fileId}
                    />
                  );
                })}
              </Stack>
            </Box>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onSubmit} disabled={busy}>
          {busy ? 'Sharing...' : 'Share'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function WorkspaceView({
  workspaceEndpointBase = '/requests/workspaces',
  workspaceListHref = '/workspaces-dashboard',
  workspaceListLabel = 'My Federated Workspaces',
  workspaceEyebrow = 'Federated Workspace',
  showWorkspaceActions = true,
  allowExchangeCardActions = showWorkspaceActions,
  showThreadFiles = true,
  ...props
}) {
  const { workspace_id: workspaceId } = useParams();
  const location = useLocation();
  const navigationParams = new URLSearchParams(location.search);
  const initialWorkstreamId = (
    location.state?.requestCapabilityId ||
    location.state?.request_capability_id ||
    navigationParams.get('workstream') ||
    ''
  );
  const initialTaskId = (
    location.state?.taskId ||
    location.state?.workstream_task_id ||
    navigationParams.get('task') ||
    ''
  );
  const normalizedWorkspaceEndpointBase = String(workspaceEndpointBase || '/requests/workspaces').replace(/\/$/, '');
  const [workspaceData, setWorkspaceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  const [createThreadDialogOpen, setCreateThreadDialogOpen] = useState(false);
  const [newThreadTitle, setNewThreadTitle] = useState('');
  const [initialRequestDialogOpen, setInitialRequestDialogOpen] = useState(false);
  const [initialRequestCorrId, setInitialRequestCorrId] = useState('');
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [fileStates, setFileStates] = useState({});
  const [selectedCorrId, setSelectedCorrId] = useState('');
  const [threadData, setThreadData] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadActionLoading, setThreadActionLoading] = useState(false);
  const [managedAssets, setManagedAssets] = useState([]);
  const [managedAssetsLoading, setManagedAssetsLoading] = useState(false);
  const [tdpInventory, setTdpInventory] = useState([]);
  const [tdpInventoryLoading, setTdpInventoryLoading] = useState(false);
  const [navigationStateConsumed, setNavigationStateConsumed] = useState(false);
  const [managedCapabilities, setManagedCapabilities] = useState([]);
  const [managedCapabilitiesLoading, setManagedCapabilitiesLoading] = useState(false);
  const [advertisementDialogOpen, setAdvertisementDialogOpen] = useState(false);
  const [advertisementKind, setAdvertisementKind] = useState('capability');
  const [advertisementTargetId, setAdvertisementTargetId] = useState('');
  const [advertisementNotes, setAdvertisementNotes] = useState('');
  const [advertising, setAdvertising] = useState(false);
  const [sharingGrants, setSharingGrants] = useState([]);
  const [sharingLoading, setSharingLoading] = useState(false);
  const [sharingBusy, setSharingBusy] = useState(false);
  const [shareDialog, setShareDialog] = useState({ open: false, resourceType: '', title: '' });
  const [shareForm, setShareForm] = useState(emptyShareForm);
  const isDirectRequestWorkspace = workspaceData?.workspace_kind === 'direct_capability_request_v1' ||
    workspaceData?.direct_request_workspace === true;
  const workspaceKindKnown = Boolean(workspaceData);

  const fetchWorkspace = async ({ background = false } = {}) => {
    if (!background) {
      setLoading(true);
    }
    setError('');

    try {
      const response = await axiosInstance.get(`${normalizedWorkspaceEndpointBase}/${workspaceId}`);
      setWorkspaceData(response.data);
    } catch (err) {
      console.error('Failed to fetch workspace details', err);
      setError('Unable to load this workspace right now.');
    } finally {
      if (!background) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    let isActive = true;

    const loadWorkspace = async () => {
      if (!isActive) {
        return;
      }

      await fetchWorkspace();
    };

    loadWorkspace();

    return () => {
      isActive = false;
    };
  }, [workspaceId, normalizedWorkspaceEndpointBase]);

  useEffect(() => {
    if (!workspaceKindKnown || isDirectRequestWorkspace) {
      setManagedAssets([]);
      setManagedAssetsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setManagedAssetsLoading(true);

    axiosInstance.get('/partners/user/assets')
      .then((response) => {
        if (!cancelled) {
          setManagedAssets(Array.isArray(response.data?.assets) ? response.data.assets : []);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch managed assets', err);
        if (!cancelled) {
          setManagedAssets([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setManagedAssetsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [workspaceKindKnown, isDirectRequestWorkspace]);

  useEffect(() => {
    if (!workspaceKindKnown || isDirectRequestWorkspace) {
      setTdpInventory([]);
      setTdpInventoryLoading(false);
      return undefined;
    }

    let cancelled = false;
    setTdpInventoryLoading(true);

    axiosInstance.get('/tdps')
      .then((response) => {
        if (!cancelled) {
          setTdpInventory(Array.isArray(response.data?.tdps) ? response.data.tdps : []);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch xTDP inventory', err);
        if (!cancelled) {
          setTdpInventory([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setTdpInventoryLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [workspaceKindKnown, isDirectRequestWorkspace]);

  useEffect(() => {
    if (!workspaceKindKnown || showWorkspaceActions || isDirectRequestWorkspace) {
      setManagedCapabilities([]);
      setManagedCapabilitiesLoading(false);
      return undefined;
    }

    let cancelled = false;
    setManagedCapabilitiesLoading(true);

    axiosInstance.get('/partners/user/capabilities')
      .then((response) => {
        if (!cancelled) {
          setManagedCapabilities(Array.isArray(response.data?.capabilities) ? response.data.capabilities : []);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch provider capabilities', err);
        if (!cancelled) {
          setManagedCapabilities([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setManagedCapabilitiesLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [workspaceKindKnown, showWorkspaceActions, isDirectRequestWorkspace]);

  const workspace = workspaceData?.workspace;
  const workspacePartner = workspace?.created_by_partner || {};
  const threads = useMemo(
    () => workspaceData?.threads || [],
    [workspaceData],
  );
  const selectedThread = useMemo(
    () => threads.find((thread) => thread.corr_id === selectedCorrId) || threads[0] || null,
    [threads, selectedCorrId],
  );
  const workspaceStatus = String(workspace?.status || '').toLowerCase();
  const isWorkspaceSharedToMe = Boolean(workspace?.shared_access);
  const isWorkspaceDeactivated = workspaceStatus === 'deactivated';
  const selectedThreadStatus = String((threadData || selectedThread)?.status || '').toLowerCase();
  const isSelectedThreadClosed = selectedThreadStatus === 'closed' || selectedThreadStatus === 'archived';
  const isThreadSharedToMe = Boolean((threadData || selectedThread)?.shared_access);
  const isReadOnlySharedAccess = isWorkspaceSharedToMe || isThreadSharedToMe;
  const canManageWorkspaceSharing = showWorkspaceActions && !isDirectRequestWorkspace && !isReadOnlySharedAccess;
  const workspacePartnerName = workspacePartner.display_name || workspacePartner.legal_name || workspace?.created_by_partner_id || 'Unknown';
  const workspacePartnerId = workspacePartner.partner_id || workspace?.created_by_partner_id || 'Unknown';
  const workspacePartnerLabel = workspacePartnerName === workspacePartnerId
    ? workspacePartnerId
    : `${workspacePartnerName} (${workspacePartnerId})`;
  const activeThread = threadData || selectedThread || {};
  const activeCategoryId = String(activeThread?.metadata?.category_id || '').trim();
  const advertisementCapabilityOptions = useMemo(
    () => managedCapabilities.filter((capability) => (
      !activeCategoryId || String(capability.category_id || '') === activeCategoryId
    )),
    [managedCapabilities, activeCategoryId],
  );
  const advertisementAssetOptions = useMemo(
    () => managedAssets.filter((asset) => {
      if (!activeCategoryId) {
        return true;
      }
      const capability = managedCapabilities.find(
        (item) => item.capability_definition_id === asset.capability_definition_id,
      );
      return !capability || String(capability.category_id || '') === activeCategoryId;
    }),
    [managedAssets, managedCapabilities, activeCategoryId],
  );
  const advertisementOptions = advertisementKind === 'asset'
    ? advertisementAssetOptions
    : advertisementCapabilityOptions;

  const loadSharingGrants = async () => {
    if (!workspaceId || !canManageWorkspaceSharing) {
      setSharingGrants([]);
      return;
    }

    setSharingLoading(true);
    try {
      const response = await axiosInstance.get('/sharing/grants', {
        params: { workspace_id: workspaceId },
      });
      setSharingGrants(Array.isArray(response.data?.grants) ? response.data.grants : []);
    } catch (err) {
      console.error('Failed to load sharing grants', err);
      setSharingGrants([]);
    } finally {
      setSharingLoading(false);
    }
  };

  useEffect(() => {
    loadSharingGrants();
  }, [workspaceId, canManageWorkspaceSharing]);

  useEffect(() => {
    if (!threads.length) {
      setSelectedCorrId('');
      setThreadData(null);
      return;
    }

    const selectedStillExists = threads.some((thread) => thread.corr_id === selectedCorrId);
    if (!selectedCorrId || !selectedStillExists) {
      setSelectedCorrId(threads[0].corr_id);
    }
  }, [threads, selectedCorrId]);

  useEffect(() => {
    const state = location.state || {};
    const shouldOpenInitialRequest = Boolean(state.openInitialRequest);
    const targetCorrId = state.corr_id || state.corrId || '';
    if (navigationStateConsumed || !shouldOpenInitialRequest || !threads.length) {
      return;
    }
    const corrId = targetCorrId || threads[0]?.corr_id || '';
    if (!corrId) {
      return;
    }
    setSelectedCorrId(corrId);
    setInitialRequestCorrId(corrId);
    setInitialRequestDialogOpen(true);
    setNavigationStateConsumed(true);
  }, [location.state, navigationStateConsumed, threads]);

  useEffect(() => {
    if (!selectedCorrId) {
      setThreadData(null);
      return undefined;
    }

    let cancelled = false;
    setThreadLoading(true);

    axiosInstance.get(`/threads/${encodeURIComponent(selectedCorrId)}`)
      .then((response) => {
        if (!cancelled) {
          setThreadData(response.data);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch thread details', err);
        if (!cancelled) {
          setThreadData(null);
          setActionError(err?.response?.data?.message || 'Unable to load this thread right now.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setThreadLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedCorrId]);

  const getFileState = (corrId) => (
    fileStates[corrId] || {
      selectedUploadFile: null,
      filesLoading: false,
      uploadingFile: false,
      downloadingFile: false,
      deletingFile: false,
      deletingFilename: '',
      error: '',
      success: '',
      listedFiles: [],
    }
  );

  const updateFileState = (corrId, updater) => {
    setFileStates((current) => {
      const previous = current[corrId] || getFileState(corrId);
      const next = typeof updater === 'function' ? updater(previous) : updater;
      return {
        ...current,
        [corrId]: {
          ...previous,
          ...next,
        },
      };
    });
  };

  const triggerBrowserDownload = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    if (filename) {
      link.download = filename;
    }
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const triggerBlobDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    triggerBrowserDownload(url, filename);
    window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  };

  const handleListFiles = async (corrId) => {
    updateFileState(corrId, {
      filesLoading: true,
      error: '',
      success: '',
    });

    try {
      const response = await axiosInstance.get(`/requests/files/${encodeURIComponent(corrId)}`);
      const files = Array.isArray(response.data?.files) ? response.data.files : [];
      updateFileState(corrId, {
        listedFiles: files,
        success: files.length
          ? `Found ${files.length} file${files.length === 1 ? '' : 's'} for this correlation thread.`
          : 'No files found for this correlation thread.',
      });
    } catch (err) {
      console.error('Failed to list files', err);
      updateFileState(corrId, {
        listedFiles: [],
        error: err?.response?.data?.message || 'Unable to list files for this correlation thread.',
      });
    } finally {
      updateFileState(corrId, { filesLoading: false });
    }
  };

  useEffect(() => {
    const corrId = selectedThread?.corr_id;
    if (!showThreadFiles || !corrId || fileStates[corrId]) {
      return;
    }

    handleListFiles(corrId);
  }, [selectedThread?.corr_id, fileStates, handleListFiles, showThreadFiles]);

  const handleUploadFile = async (corrId) => {
    const currentFileState = getFileState(corrId);
    if (!currentFileState.selectedUploadFile) {
      updateFileState(corrId, {
        error: 'Choose a file to upload first.',
        success: '',
      });
      return;
    }

    updateFileState(corrId, {
      uploadingFile: true,
      error: '',
      success: '',
    });

    try {
      const response = await axiosInstance.post(`/requests/files/${encodeURIComponent(corrId)}/upload`, {
        filename: currentFileState.selectedUploadFile.name,
      });

      const formData = new FormData();
      Object.entries(response.data.fields || {}).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('file', currentFileState.selectedUploadFile);

      const uploadResponse = await fetch(response.data.url, {
        method: response.data.method || 'POST',
        body: formData,
      });
      if (!uploadResponse.ok) {
        throw new Error(`Upload failed with status ${uploadResponse.status}.`);
      }

      updateFileState(corrId, {
        success: `Uploaded ${currentFileState.selectedUploadFile.name}.`,
        selectedUploadFile: null,
      });
      await handleListFiles(corrId);
      await refreshThreadDetail(corrId);
    } catch (err) {
      console.error('Failed to upload file', err);
      updateFileState(corrId, {
        error: err?.response?.data?.message || err?.message || 'Unable to upload file.',
      });
    } finally {
      updateFileState(corrId, { uploadingFile: false });
    }
  };

  const handleDownloadFile = async (corrId, filename) => {
    const normalizedFilename = (filename || '').trim();
    if (!normalizedFilename) {
      updateFileState(corrId, {
        error: 'Missing filename for download.',
        success: '',
      });
      return;
    }

    updateFileState(corrId, {
      downloadingFile: true,
      error: '',
      success: '',
    });

    try {
      const response = await axiosInstance.post(`/requests/files/${encodeURIComponent(corrId)}/download`, {
        filename: normalizedFilename,
      });
      triggerBrowserDownload(response.data.url, normalizedFilename);
      updateFileState(corrId, {
        success: `Started download for ${normalizedFilename}.`,
      });
    } catch (err) {
      console.error('Failed to download file', err);
      updateFileState(corrId, {
        error: err?.response?.data?.message || err?.message || 'Unable to download file.',
      });
    } finally {
      updateFileState(corrId, { downloadingFile: false });
    }
  };

  const handleCreateThreadFileDownloadUrl = async (filename) => {
    const corrId = threadData?.corr_id || selectedCorrId;
    const normalizedFilename = (filename || '').trim();
    if (!corrId || !normalizedFilename) {
      throw new Error('Missing thread file context for preview.');
    }
    const response = await axiosInstance.post(`/requests/files/${encodeURIComponent(corrId)}/download`, {
      filename: normalizedFilename,
    });
    if (!response.data?.url) {
      throw new Error('Preview link was not returned for this file.');
    }
    return response.data.url;
  };

  const handleDeleteFile = async (corrId, filename) => {
    const normalizedFilename = (filename || '').trim();
    if (!normalizedFilename) {
      updateFileState(corrId, {
        error: 'Missing filename for delete.',
        success: '',
      });
      return;
    }

    updateFileState(corrId, {
      deletingFile: true,
      deletingFilename: normalizedFilename,
      error: '',
      success: '',
    });

    try {
      await axiosInstance.post(`/requests/files/${encodeURIComponent(corrId)}/delete`, {
        filename: normalizedFilename,
      });
      updateFileState(corrId, {
        success: `Deleted ${normalizedFilename}.`,
      });
      await handleListFiles(corrId);
      await refreshThreadDetail(corrId);
    } catch (err) {
      console.error('Failed to delete file', err);
      updateFileState(corrId, {
        error: err?.response?.data?.message || err?.message || 'Unable to delete file.',
      });
    } finally {
      updateFileState(corrId, {
        deletingFile: false,
        deletingFilename: '',
      });
    }
  };

  const handleInitialRequestCreated = async ({ corrId } = {}) => {
    const refreshedCorrId = corrId || initialRequestCorrId || selectedCorrId;
    setInitialRequestDialogOpen(false);
    setInitialRequestCorrId('');
    setActionError('');
    setActionSuccess(
      refreshedCorrId
        ? `Initial request added to thread ${refreshedCorrId}.`
        : 'Initial request added to this thread.',
    );
    if (refreshedCorrId) {
      setSelectedCorrId(refreshedCorrId);
    }
    await refreshThreadDetail(refreshedCorrId);
  };

  const refreshThreadDetail = async (corrId) => {
    await fetchWorkspace({ background: true });
    if (!corrId) {
      return;
    }
    const threadResponse = await axiosInstance.get(`/threads/${encodeURIComponent(corrId)}`);
    setThreadData(threadResponse.data);
  };

  const handleCreateEmptyThread = async () => {
    if (!workspaceId || isWorkspaceDeactivated) {
      return;
    }

    const title = newThreadTitle.trim();
    if (!title) {
      setActionError('Enter a thread name before creating the thread.');
      return;
    }

    setThreadActionLoading(true);
    setActionError('');
    setActionSuccess('');

    try {
      const response = await axiosInstance.post(
        `/requests/workspaces/${encodeURIComponent(workspaceId)}/threads`,
        {
          title,
        },
      );
      const corrId = response.data?.corr_id;
      setSelectedCorrId(corrId || '');
      setCreateThreadDialogOpen(false);
      setNewThreadTitle('');
      setActionSuccess('Empty thread created.');
      await refreshThreadDetail(corrId);
    } catch (err) {
      console.error('Failed to create empty thread', err);
      setActionError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to create an empty thread right now.',
      );
    } finally {
      setThreadActionLoading(false);
    }
  };

  const handleCloseThread = async () => {
    const corrId = threadData?.corr_id || selectedCorrId;
    if (!corrId) {
      return;
    }

    setThreadActionLoading(true);
    setActionError('');
    setActionSuccess('');

    try {
      await axiosInstance.post(`/threads/${encodeURIComponent(corrId)}/close`);
      setActionSuccess('Thread closed.');
      await refreshThreadDetail(corrId);
    } catch (err) {
      console.error('Failed to close thread', err);
      setActionError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to close this thread right now.',
      );
    } finally {
      setThreadActionLoading(false);
    }
  };

  const handleReopenThread = async () => {
    const corrId = threadData?.corr_id || selectedCorrId;
    if (!corrId) {
      return;
    }

    setThreadActionLoading(true);
    setActionError('');
    setActionSuccess('');

    try {
      await axiosInstance.post(`/threads/${encodeURIComponent(corrId)}/reopen`);
      setActionSuccess('Thread reopened.');
      await refreshThreadDetail(corrId);
    } catch (err) {
      console.error('Failed to reopen thread', err);
      setActionError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to reopen this thread right now.',
      );
    } finally {
      setThreadActionLoading(false);
    }
  };

  const handleSelectCapability = async (capability = {}) => {
    const corrId = threadData?.corr_id || selectedCorrId;
    const messageUuid = capability.message_uuid;
    if (!corrId || !messageUuid) {
      setActionSuccess('');
      setActionError('Unable to select this capability because its message context is missing.');
      return;
    }

    setActionError('');
    setActionSuccess('');

    try {
      const response = await axiosInstance.post(
        `/requests/workspaces/${workspaceId}/capabilities/select`,
        {
          corr_id: corrId,
          capability_message_uuid: messageUuid,
        },
      );
      const providerName = getPartnerDisplayName(
        response.data?.allowed_partner,
        'Provider',
      );
      const capabilityName = capability.title || capability.body?.capability_name || 'the selected capability';
      setActionSuccess(`${providerName} can now access this workspace thread for ${capabilityName}.`);
      await refreshThreadDetail(corrId);
    } catch (err) {
      console.error('Failed to select capability', err);
      setActionError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to select this capability right now.',
      );
      throw err;
    }
  };

  const handleSelectAsset = async (asset = {}) => {
    const corrId = threadData?.corr_id || selectedCorrId;
    const messageUuid = asset.message_uuid;
    if (!corrId || !messageUuid) {
      setActionSuccess('');
      setActionError('Unable to select this asset because its message context is missing.');
      return;
    }

    setActionError('');
    setActionSuccess('');

    try {
      const response = await axiosInstance.post(
        `/requests/workspaces/${workspaceId}/assets/select`,
        {
          corr_id: corrId,
          asset_message_uuid: messageUuid,
        },
      );
      const providerName = getPartnerDisplayName(response.data?.allowed_partner, 'Provider');
      const assetName = asset.title || asset.body?.asset_name || 'the selected asset';
      setActionSuccess(`${providerName} can now access this workspace thread for ${assetName}.`);
      await refreshThreadDetail(corrId);
    } catch (err) {
      console.error('Failed to select asset', err);
      setActionError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to select this asset right now.',
      );
      throw err;
    }
  };

  const handlePullExchangeCard = async (card = {}, payload = {}) => {
    const corrId = threadData?.corr_id || selectedCorrId;
    const cardKey = card.card_key;
    if (!corrId || !cardKey) {
      setActionSuccess('');
      setActionError('Unable to add this Exchange Card because its thread context is missing.');
      return;
    }

    setActionError('');
    setActionSuccess('');

    try {
      await axiosInstance.post(
        `/threads/${encodeURIComponent(corrId)}/exchange-cards/${encodeURIComponent(cardKey)}/pull`,
        payload || {},
      );
      setActionSuccess(`${card.title || 'Exchange Card'} added to the thread timeline.`);
      await refreshThreadDetail(corrId);
      if (cardKey === 'initial_request') {
        setInitialRequestCorrId(corrId);
        setInitialRequestDialogOpen(true);
      }
    } catch (err) {
      console.error('Failed to pull exchange card', err);
      setActionError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to add this Exchange Card right now.',
      );
      throw err;
    }
  };

  const handleStartInitialRequest = () => {
    const corrId = threadData?.corr_id || selectedCorrId;
    if (!corrId) {
      setActionSuccess('');
      setActionError('Select a thread before filling the Initial Request.');
      return;
    }

    setActionError('');
    setActionSuccess('');
    setInitialRequestCorrId(corrId);
    setInitialRequestDialogOpen(true);
  };

  const handleOpenAdvertisementDialog = () => {
    setActionError('');
    setActionSuccess('');
    setAdvertisementKind('capability');
    setAdvertisementTargetId('');
    setAdvertisementNotes('');
    setAdvertisementDialogOpen(true);
  };

  const openShareDialog = (context) => {
    setActionError('');
    setActionSuccess('');
    setShareForm(emptyShareForm);
    setShareDialog({ open: true, ...context });
  };

  const handleShareWorkspace = () => {
    openShareDialog({
      resourceType: 'workspace',
      resourceId: workspaceId,
      workspaceId,
      title: workspace?.title || 'Workspace',
      threads,
    });
  };

  const handleShareThread = () => {
    const thread = threadData || selectedThread;
    if (!thread?.corr_id) {
      setActionError('Select a thread before sharing it.');
      return;
    }
    openShareDialog({
      resourceType: 'thread',
      resourceId: thread.corr_id,
      workspaceId,
      corrId: thread.corr_id,
      title: thread.thread_title || 'Thread',
    });
  };

  const handleShareExchangeCard = (card = {}) => {
    const corrId = threadData?.corr_id || selectedCorrId;
    if (!corrId || !card.card_key) {
      setActionError('Unable to share this Exchange Card because its thread context is missing.');
      return;
    }
    openShareDialog({
      resourceType: 'exchange_card',
      resourceId: card.thread_exchange_card_id || '',
      workspaceId,
      corrId,
      cardKey: card.card_key,
      title: card.title || 'Exchange Card',
      schemaFields: getCardSchemaFields(card),
      files: getCardFiles(card),
    });
  };

  const closeShareDialog = (force = false) => {
    if (sharingBusy && !force) {
      return;
    }
    setShareDialog({ open: false, resourceType: '', title: '' });
    setShareForm(emptyShareForm);
  };

  const handleSubmitShare = async () => {
    const targetMode = shareForm.targetMode || 'internal';
    const selectedTarget = shareForm.targetUser || {};
    const targetUserId = targetMode === 'internal' ? String(selectedTarget.user_id || '').trim() : '';
    const targetPartnerId = targetMode === 'internal' ? String(selectedTarget.partner_id || '').trim() : '';
    const targetEmail = targetMode === 'external' ? shareForm.targetEmail.trim() : '';
    if (targetMode === 'internal' && !targetUserId) {
      setActionError('Select an internal AMX user before sharing.');
      return;
    }
    if (targetMode === 'external' && !targetEmail) {
      setActionError('Enter an external AMX user email before sharing.');
      return;
    }

    const redactionPolicy = {};
    if (shareDialog.resourceType === 'workspace' && shareForm.excludedCorrIds.length) {
      redactionPolicy.excluded_corr_ids = shareForm.excludedCorrIds;
    }
    if (shareDialog.resourceType === 'exchange_card') {
      if (shareForm.redactedFields.length) {
        redactionPolicy.redacted_fields = shareForm.redactedFields;
      }
      if (shareForm.redactedFileIds.length) {
        redactionPolicy.redacted_file_ids = shareForm.redactedFileIds;
      }
    }

    setSharingBusy(true);
    setActionError('');
    setActionSuccess('');

    try {
      await axiosInstance.post('/sharing/grants', {
        resource_type: shareDialog.resourceType,
        resource_id: shareDialog.resourceId || undefined,
        workspace_id: shareDialog.workspaceId || workspaceId,
        corr_id: shareDialog.corrId || undefined,
        card_key: shareDialog.cardKey || undefined,
        target_scope: targetMode,
        target_user_id: targetUserId || undefined,
        target_email: targetEmail || undefined,
        target_partner_id: targetPartnerId || undefined,
        permission: shareForm.permission,
        expires_at: shareForm.expiresAt ? new Date(shareForm.expiresAt).toISOString() : undefined,
        redaction_policy: redactionPolicy,
        metadata: {
          source: 'workspace_view',
          target_mode: targetMode,
        },
      });
      setActionSuccess(`${shareDialog.title || 'Resource'} shared.`);
      closeShareDialog(true);
      await loadSharingGrants();
    } catch (err) {
      console.error('Failed to create sharing grant', err);
      setActionError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to share this resource right now.',
      );
    } finally {
      setSharingBusy(false);
    }
  };

  const handleRevokeSharingGrant = async (grant) => {
    if (!grant?.sharing_grant_id) {
      return;
    }

    setSharingBusy(true);
    setActionError('');
    setActionSuccess('');
    try {
      await axiosInstance.post(`/sharing/grants/${encodeURIComponent(grant.sharing_grant_id)}/revoke`);
      setActionSuccess('Sharing access revoked.');
      await loadSharingGrants();
    } catch (err) {
      console.error('Failed to revoke sharing grant', err);
      setActionError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to revoke sharing access right now.',
      );
    } finally {
      setSharingBusy(false);
    }
  };

  const handleSubmitManualAdvertisement = async () => {
    const corrId = threadData?.corr_id || selectedCorrId;
    if (!corrId) {
      setActionSuccess('');
      setActionError('Select a provider thread before adding an advertisement.');
      return;
    }
    if (!advertisementTargetId) {
      setActionSuccess('');
      setActionError('Choose a capability or asset before adding an advertisement.');
      return;
    }

    setAdvertising(true);
    setActionError('');
    setActionSuccess('');

    try {
      const payload = {
        request_content: (threadData || selectedThread)?.initial_request?.body || {},
      };
      if (advertisementKind === 'asset') {
        payload.asset_id = advertisementTargetId;
      } else {
        payload.capability_definition_id = advertisementTargetId;
      }
      if (advertisementNotes.trim()) {
        payload.notes = advertisementNotes.trim();
      }

      await axiosInstance.post(
        `/provider/threads/${encodeURIComponent(corrId)}/advertisements`,
        payload,
      );
      setAdvertisementDialogOpen(false);
      setAdvertisementTargetId('');
      setAdvertisementNotes('');
      setActionSuccess('Advertisement added to the provider thread.');
      await refreshThreadDetail(corrId);
    } catch (err) {
      console.error('Failed to create manual advertisement', err);
      setActionError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to add this advertisement right now.',
      );
    } finally {
      setAdvertising(false);
    }
  };

  const handleCompleteExchangeCard = async (card = {}, body = null) => {
    const corrId = threadData?.corr_id || selectedCorrId;
    const cardKey = card.card_key;
    if (!corrId || !cardKey) {
      setActionSuccess('');
      setActionError('Unable to complete this Exchange Card because its thread context is missing.');
      return;
    }

    setActionError('');
    setActionSuccess('');

    try {
      await axiosInstance.post(
        `/threads/${encodeURIComponent(corrId)}/exchange-cards/${encodeURIComponent(cardKey)}/complete`,
        {
          body: body || {
            confirmed: true,
          },
        },
      );
      setActionSuccess(`${card.title || 'Exchange Card'} confirmed.`);
      await refreshThreadDetail(corrId);
    } catch (err) {
      console.error('Failed to complete exchange card', err);
      setActionError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to complete this Exchange Card right now.',
      );
      throw err;
    }
  };

  const handlePublishExchangeCard = async (card = {}) => {
    const corrId = threadData?.corr_id || selectedCorrId;
    const cardKey = card.card_key;
    if (!corrId || !cardKey) {
      setActionSuccess('');
      setActionError('Unable to publish this Exchange Card because its thread context is missing.');
      return;
    }

    setActionError('');
    setActionSuccess('');

    try {
      await axiosInstance.post(
        `/threads/${encodeURIComponent(corrId)}/exchange-cards/${encodeURIComponent(cardKey)}/publish`,
        {},
      );
      setActionSuccess(`${card.title || 'Exchange Card'} published.`);
      await refreshThreadDetail(corrId);
    } catch (err) {
      console.error('Failed to publish exchange card', err);
      setActionError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to publish this Exchange Card right now.',
      );
      throw err;
    }
  };

  const handleRemoveExchangeCard = async (card = {}) => {
    const corrId = threadData?.corr_id || selectedCorrId;
    const cardKey = card.card_key;
    if (!corrId || !cardKey) {
      setActionSuccess('');
      setActionError('Unable to remove this Exchange Card because its thread context is missing.');
      return;
    }
    if (card.status === 'completed') {
      setActionSuccess('');
      setActionError('Completed Exchange Cards cannot be removed from the thread.');
      return;
    }

    const confirmed = window.confirm(
      `Remove "${card.title || 'Exchange Card'}" from this thread timeline?`,
    );
    if (!confirmed) {
      return;
    }

    setActionError('');
    setActionSuccess('');

    try {
      await axiosInstance.delete(
        `/threads/${encodeURIComponent(corrId)}/exchange-cards/${encodeURIComponent(cardKey)}`,
      );
      setActionSuccess(`${card.title || 'Exchange Card'} removed from the thread timeline.`);
      await refreshThreadDetail(corrId);
    } catch (err) {
      console.error('Failed to remove exchange card', err);
      setActionError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to remove this Exchange Card right now.',
      );
      throw err;
    }
  };

  const handlePreparePrintJob = async (card = {}, body = {}) => {
    const corrId = threadData?.corr_id || selectedCorrId;
    if (!corrId) {
      setActionSuccess('');
      setActionError('Select a thread before preparing a print job.');
      return;
    }

    setActionError('');
    setActionSuccess('');

    try {
      await axiosInstance.post(
        `/threads/${encodeURIComponent(corrId)}/print-jobs/prepare`,
        body,
      );
      setActionSuccess(`${card.title || 'Print file'} sent to the QNI printer for staging.`);
      await refreshThreadDetail(corrId);
    } catch (err) {
      console.error('Failed to prepare print job', err);
      setActionError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to prepare this print job right now.',
      );
      throw err;
    }
  };

  const handleEditExchangeCard = async (card = {}, payload = {}) => {
    const corrId = threadData?.corr_id || selectedCorrId;
    const cardKey = card.card_key;
    if (!corrId || !cardKey) {
      setActionSuccess('');
      setActionError('Unable to edit this Exchange Card because its thread context is missing.');
      return;
    }

    setActionError('');
    setActionSuccess('');

    try {
      const response = await axiosInstance.post(
        `/threads/${encodeURIComponent(corrId)}/exchange-cards/${encodeURIComponent(cardKey)}/edit`,
        payload,
      );
      setActionSuccess(
        response.data?.resubmit_required
          ? `${card.title || 'Exchange Card'} edited. Replacement draft thread ${response.data?.replacement_corr_id || ''} is ready for resubmission.`
          : `${card.title || 'Exchange Card'} edited.`,
      );
      if (response.data?.replacement_corr_id) {
        setSelectedCorrId(response.data.replacement_corr_id);
      }
      await refreshThreadDetail(response.data?.replacement_corr_id || corrId);
    } catch (err) {
      console.error('Failed to edit exchange card', err);
      setActionError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to edit this Exchange Card right now.',
      );
      throw err;
    }
  };

  const handleStartPrintJob = async (card = {}, printJob = {}) => {
    const corrId = threadData?.corr_id || selectedCorrId;
    const printJobId = printJob.print_job_id;
    if (!corrId || !printJobId) {
      setActionSuccess('');
      setActionError('Choose a ready print job before sending the start command.');
      return;
    }

    setActionError('');
    setActionSuccess('');

    try {
      await axiosInstance.post(
        `/threads/${encodeURIComponent(corrId)}/print-jobs/${encodeURIComponent(printJobId)}/start`,
        { print_job_id: printJobId, start_confirmed: true },
      );
      setActionSuccess(`${card.title || 'Print job'} start command sent.`);
      await refreshThreadDetail(corrId);
    } catch (err) {
      console.error('Failed to start print job', err);
      setActionError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to start this print job right now.',
      );
      throw err;
    }
  };

  const handleExportExchangeCard = async (card = {}) => {
    const corrId = threadData?.corr_id || selectedCorrId;
    const cardKey = card.card_key;
    if (!corrId || !cardKey) {
      setActionSuccess('');
      setActionError('Unable to export this Exchange Card because its thread context is missing.');
      return;
    }

    setActionError('');
    setActionSuccess('');

    try {
      const response = await axiosInstance.get(
        `/threads/${encodeURIComponent(corrId)}/exchange-cards/${encodeURIComponent(cardKey)}/export`,
        { responseType: 'blob' },
      );
      triggerBlobDownload(
        response.data,
        `amx-exchange-card-${cardKey}.pdf`,
      );
      setActionSuccess(`${card.title || 'Exchange Card'} export started.`);
    } catch (err) {
      console.error('Failed to export exchange card', err);
      setActionError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to export this Exchange Card right now.',
      );
      throw err;
    }
  };

  const handleViewExchangeCardRevisions = async (card = {}) => {
    const corrId = threadData?.corr_id || selectedCorrId;
    const cardKey = card.card_key;
    if (!corrId || !cardKey) {
      setActionSuccess('');
      setActionError('Unable to load edit history because this card context is missing.');
      return [];
    }

    setActionError('');

    try {
      const response = await axiosInstance.get(
        `/threads/${encodeURIComponent(corrId)}/exchange-cards/${encodeURIComponent(cardKey)}/revisions`,
      );
      return Array.isArray(response.data?.revisions) ? response.data.revisions : [];
    } catch (err) {
      console.error('Failed to load exchange card revisions', err);
      setActionError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to load edit history for this Exchange Card right now.',
      );
      throw err;
    }
  };

  const handleConfirmPrintPickup = async (card = {}, printJob = {}, body = {}) => {
    const corrId = threadData?.corr_id || selectedCorrId;
    const printJobId = printJob.print_job_id;
    if (!corrId || !printJobId) {
      setActionSuccess('');
      setActionError('Choose a completed print job before confirming pickup.');
      return;
    }

    setActionError('');
    setActionSuccess('');

    try {
      await axiosInstance.post(
        `/threads/${encodeURIComponent(corrId)}/print-jobs/${encodeURIComponent(printJobId)}/pickup-confirm`,
        {
          print_job_id: printJobId,
          pickup_confirmed: true,
          ...body,
        },
      );
      setActionSuccess(`${card.title || 'Print pickup'} confirmed.`);
      await refreshThreadDetail(corrId);
    } catch (err) {
      console.error('Failed to confirm print pickup', err);
      setActionError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to confirm print pickup right now.',
      );
      throw err;
    }
  };

  const handleDeactivateWorkspace = async () => {
    setDeactivating(true);
    setActionError('');
    setActionSuccess('');

    try {
      await axiosInstance.post(`/requests/workspaces/${workspaceId}/deactivate`);
      setDeactivateDialogOpen(false);
      setActionSuccess('Workspace deactivated successfully.');
      await fetchWorkspace({ background: true });
    } catch (err) {
      console.error('Failed to deactivate workspace', err);
      setActionError(
        err?.response?.data?.detail ||
          err?.message ||
          'Unable to deactivate this workspace right now.',
      );
    } finally {
      setDeactivating(false);
    }
  };

  const renderThreadFilesPanel = (group) => {
    const fileState = getFileState(group.corr_id);

    return (
      <Box
        sx={{
          p: { xs: 1.5, md: 2 },
          borderRadius: 2,
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.035),
          border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <Stack spacing={1.5}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            Thread Files
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <Button variant="outlined" component="label">
              {fileState.selectedUploadFile ? `Selected: ${fileState.selectedUploadFile.name}` : 'Choose Upload File'}
              <input
                hidden
                type="file"
                onChange={(event) => {
                  const file = event.target.files?.[0] || null;
                  updateFileState(group.corr_id, { selectedUploadFile: file });
                }}
              />
            </Button>
            <Button
              variant="contained"
              onClick={() => handleUploadFile(group.corr_id)}
              disabled={fileState.uploadingFile}
            >
              {fileState.uploadingFile ? 'Uploading...' : 'Upload File'}
            </Button>
          </Stack>
          {fileState.error ? <Alert severity="error">{fileState.error}</Alert> : null}
          {fileState.success ? <Alert severity="success">{fileState.success}</Alert> : null}
          <Stack spacing={1}>
            {fileState.filesLoading ? (
              <Typography variant="caption" color="text.secondary">
                Loading files...
              </Typography>
            ) : fileState.listedFiles.length ? (
              fileState.listedFiles.map((file) => (
                <Box
                  key={file.key}
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    bgcolor: (theme) => alpha(theme.palette.background.paper, 0.9),
                    border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.72)}`,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-all' }}>
                    {file.filename}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {file.size_bytes ?? 0} bytes
                  </Typography>
                  <Button
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={() => handleDownloadFile(group.corr_id, file.filename)}
                    disabled={fileState.downloadingFile}
                  >
                    Download
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    sx={{ mt: 1, ml: 1 }}
                    onClick={() => handleDeleteFile(group.corr_id, file.filename)}
                    disabled={
                      fileState.deletingFile &&
                      fileState.deletingFilename === file.filename
                    }
                  >
                    {fileState.deletingFile &&
                    fileState.deletingFilename === file.filename
                      ? 'Deleting...'
                      : 'Delete'}
                  </Button>
                </Box>
              ))
            ) : (
              <Typography variant="caption" color="text.secondary">
                No files listed for this thread yet.
              </Typography>
            )}
          </Stack>
        </Stack>
      </Box>
    );
  };

  const renderThreadsPanel = () => (
    <Card sx={{ borderRadius: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Threads
        </Typography>
        <Stack spacing={1}>
          {threads.length ? (
            threads.map((thread) => {
              const selected = selectedThread?.corr_id === thread.corr_id;
              const threadStatus = String(thread.status || 'active').toLowerCase();
              const closed = threadStatus === 'closed' || threadStatus === 'archived';

              return (
                <ButtonBase
                  key={thread.corr_id}
                  onClick={() => setSelectedCorrId(thread.corr_id)}
                  sx={{
                    width: '100%',
                    textAlign: 'left',
                    borderRadius: 2,
                    border: (theme) => `1px solid ${alpha(
                      selected ? theme.palette.primary.main : theme.palette.divider,
                      selected ? 0.62 : 0.78,
                    )}`,
                    bgcolor: (theme) => alpha(
                      selected ? theme.palette.primary.main : theme.palette.background.paper,
                      selected ? 0.065 : 0.6,
                    ),
                    p: 1.25,
                    alignItems: 'stretch',
                  }}
                >
                  <Box sx={{ minWidth: 0, width: '100%' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 850, wordBreak: 'break-word' }}>
                      {thread.thread_title || 'Correlation Thread'}
                    </Typography>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.35 }}>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(thread.updated_at || thread.created_at)}
                      </Typography>
                      {closed ? (
                        <Chip
                          size="small"
                          label={threadStatus === 'archived' ? 'Archived' : 'Closed'}
                          color="default"
                          variant="outlined"
                          sx={{ height: 20 }}
                        />
                      ) : null}
                    </Stack>
                  </Box>
                </ButtonBase>
              );
            })
          ) : (
            <Typography variant="body2" color="text.secondary">
              No workspace threads yet.
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Header
            breadcrumbItems={[
              { label: 'AMX xConsole', href: '/' },
              { label: workspaceListLabel, href: workspaceListHref },
              { label: workspace?.title || 'Workspace View' },
            ]}
          />

          <Stack spacing={2}>
            {workspace ? <ClassificationBanner isCui={Boolean(workspace.is_cui)} /> : null}
            {workspace && !isDirectRequestWorkspace ? <Box
              sx={{
                p: { xs: 2, md: 2.25 },
                borderRadius: 2,
                bgcolor: 'background.paper',
                border: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Stack spacing={1.15} sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                    {workspaceEyebrow}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 850, wordBreak: 'break-word' }}>
                  {workspace?.title || 'Workspace View'}
                </Typography>
                {workspace?.description ? (
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                    {workspace.description}
                  </Typography>
                ) : null}
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    icon={<HubRoundedIcon />}
                    label={formatDirectLabel(workspace?.status || 'unknown')}
                    color={directStatusColor(workspace?.status)}
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    icon={<ShieldMoonRoundedIcon />}
                    label={workspace?.is_cui ? 'CUI' : 'Unclassified'}
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    icon={<ScheduleRoundedIcon />}
                    label={`Updated ${formatDate(workspace?.updated_at)}`}
                    variant="outlined"
                  />
                </Stack>
              </Stack>
            </Box> : null}

            {error ? <Alert severity="error">{error}</Alert> : null}
            {actionError ? <Alert severity="error">{actionError}</Alert> : null}
            {actionSuccess ? <Alert severity="success">{actionSuccess}</Alert> : null}

            {loading ? (
              <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
                <CircularProgress />
                <Typography color="text.secondary">
                  Building workspace activity stream...
                </Typography>
              </Stack>
            ) : null}

            {!loading && workspace && isDirectRequestWorkspace ? (
              <DirectRequestWorkspaceContent
                workspaceData={workspaceData}
                onRefresh={() => fetchWorkspace({ background: true })}
                initialWorkstreamId={initialWorkstreamId}
                initialTaskId={initialTaskId}
              />
            ) : null}

            {!loading && workspace && !isDirectRequestWorkspace ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', xl: '340px minmax(0, 1fr)' },
                  gap: 3,
                  alignItems: 'start',
                }}
              >
                <Stack spacing={2} sx={{ position: { xl: 'sticky' }, top: { xl: 88 } }}>
                  <Card sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Workspace Signals
                      </Typography>
                      <Stack spacing={2}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <ScheduleRoundedIcon color="primary" fontSize="small" />
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Updated
                            </Typography>
                            <Typography variant="body2">{formatDate(workspace.updated_at)}</Typography>
                          </Box>
                        </Stack>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <ForumRoundedIcon color="primary" fontSize="small" />
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Partner
                            </Typography>
                            <Typography variant="body2">{workspacePartnerLabel}</Typography>
                          </Box>
                        </Stack>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <FingerprintRoundedIcon color="primary" fontSize="small" />
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Workspace ID
                            </Typography>
                            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                              {workspace.workspace_id}
                            </Typography>
                          </Box>
                        </Stack>
                      </Stack>

                      {showWorkspaceActions ? (
                        <>
                          <Divider sx={{ my: 2.5 }} />

                          <Typography variant="subtitle2" gutterBottom>
                            Workspace Actions
                          </Typography>
                          <Stack spacing={1.5}>
                            {isReadOnlySharedAccess ? (
                              <Alert severity="info">
                                This resource is shared with you. Editing and workspace administration are disabled.
                              </Alert>
                            ) : null}
                            {!isWorkspaceDeactivated ? (
                              <Button
                                variant="contained"
                                startIcon={<AddCircleRoundedIcon />}
                                onClick={() => {
                                  setActionError('');
                                  setActionSuccess('');
                                  setNewThreadTitle(`Thread ${threads.length + 1}`);
                                  setCreateThreadDialogOpen(true);
                                }}
                                disabled={threadActionLoading || isReadOnlySharedAccess}
                              >
                                Create New Thread
                              </Button>
                            ) : null}
                            {canManageWorkspaceSharing ? (
                              <>
                                <Button
                                  variant="outlined"
                                  startIcon={<ShareRoundedIcon />}
                                  onClick={handleShareWorkspace}
                                  disabled={sharingBusy}
                                >
                                  Share Workspace
                                </Button>
                                <Button
                                  variant="outlined"
                                  startIcon={<ShareRoundedIcon />}
                                  onClick={handleShareThread}
                                  disabled={!selectedThread || sharingBusy}
                                >
                                  Share Selected Thread
                                </Button>
                              </>
                            ) : null}
                            {selectedThread ? (
                              isSelectedThreadClosed ? (
                                <Button
                                  variant="outlined"
                                  startIcon={<RestartAltRoundedIcon />}
                                  onClick={handleReopenThread}
                                  disabled={threadActionLoading || isWorkspaceDeactivated || isReadOnlySharedAccess}
                                >
                                  {threadActionLoading ? 'Working...' : 'Reopen Thread'}
                                </Button>
                              ) : (
                                <Button
                                  variant="outlined"
                                  color="inherit"
                                  startIcon={<CloseRoundedIcon />}
                                  onClick={handleCloseThread}
                                  disabled={threadActionLoading || isWorkspaceDeactivated || isReadOnlySharedAccess}
                                >
                                  {threadActionLoading ? 'Working...' : 'Close Thread'}
                                </Button>
                              )
                            ) : null}
                            <Button
                              variant="contained"
                              color="error"
                              startIcon={<BlockRoundedIcon />}
                              onClick={() => setDeactivateDialogOpen(true)}
                              disabled={isWorkspaceDeactivated || deactivating || isReadOnlySharedAccess}
                            >
                              {isWorkspaceDeactivated ? 'Workspace Deactivated' : 'Deactivate Workspace'}
                            </Button>
                            <Typography variant="caption" color="text.secondary">
                              {isWorkspaceDeactivated
                                ? 'Deactivated workspaces cannot receive new requests.'
                                : 'Deactivation closes this workspace to further collaboration activity.'}
                            </Typography>
                            {canManageWorkspaceSharing ? (
                              <Box sx={{ pt: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Shared With
                                </Typography>
                                {sharingLoading ? (
                                  <Typography variant="caption" color="text.secondary">
                                    Loading sharing grants...
                                  </Typography>
                                ) : sharingGrants.length ? (
                                  <Stack spacing={1}>
                                    {sharingGrants.map((grant) => (
                                      <Box
                                        key={grant.sharing_grant_id}
                                        sx={{
                                          p: 1,
                                          borderRadius: 1.5,
                                          border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.75)}`,
                                        }}
                                      >
                                        <Stack spacing={0.75}>
                                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                            {grant.target_label || grant.target_email || grant.target_partner_id || 'Shared target'}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            {grant.resource_type} / {grant.permission} / {grant.status}
                                          </Typography>
                                          <Button
                                            size="small"
                                            color="error"
                                            variant="text"
                                            onClick={() => handleRevokeSharingGrant(grant)}
                                            disabled={sharingBusy || grant.status === 'revoked'}
                                          >
                                            Revoke
                                          </Button>
                                        </Stack>
                                      </Box>
                                    ))}
                                  </Stack>
                                ) : (
                                  <Typography variant="caption" color="text.secondary">
                                    No active sharing grants for this workspace.
                                  </Typography>
                                )}
                              </Box>
                            ) : null}
                          </Stack>
                        </>
                      ) : null}

                      {!showWorkspaceActions ? (
                        <>
                          <Divider sx={{ my: 2.5 }} />

                          <Typography variant="subtitle2" gutterBottom>
                            Provider Actions
                          </Typography>
                          <Stack spacing={1.5}>
                            <Button
                              variant="contained"
                              startIcon={<AddCircleRoundedIcon />}
                              onClick={handleOpenAdvertisementDialog}
                              disabled={
                                !selectedThread ||
                                isSelectedThreadClosed ||
                                managedCapabilitiesLoading ||
                                managedAssetsLoading ||
                                advertising
                              }
                            >
                              Add Advertisement
                            </Button>
                            <Typography variant="caption" color="text.secondary">
                              Offer window: {formatDate(activeThread?.offers_accepted_expiration)}
                            </Typography>
                          </Stack>
                        </>
                      ) : null}
                    </CardContent>
                  </Card>

                  {renderThreadsPanel()}
                </Stack>

                <Stack spacing={3}>
                  {selectedThread ? (
                    threadLoading ? (
                      <Card sx={{ borderRadius: 2 }}>
                        <CardContent>
                          <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
                            <CircularProgress />
                            <Typography color="text.secondary">
                              Loading thread cards...
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    ) : (
                      <ThreadCardsView
                        key={selectedThread.corr_id}
                        thread={threadData || selectedThread}
                        filesPanel={showThreadFiles ? renderThreadFilesPanel(selectedThread) : null}
                        threadFiles={getFileState(selectedThread.corr_id).listedFiles}
                        tdpInventory={tdpInventory}
                        tdpInventoryLoading={tdpInventoryLoading}
                        onSelectCapability={showWorkspaceActions ? handleSelectCapability : null}
                        onSelectAsset={showWorkspaceActions ? handleSelectAsset : null}
                        onPullExchangeCard={allowExchangeCardActions ? handlePullExchangeCard : null}
                        onCompleteExchangeCard={allowExchangeCardActions ? handleCompleteExchangeCard : null}
                        onCreateThreadFileDownloadUrl={showThreadFiles ? handleCreateThreadFileDownloadUrl : null}
                        onPublishExchangeCard={allowExchangeCardActions ? handlePublishExchangeCard : null}
                        onRemoveExchangeCard={allowExchangeCardActions ? handleRemoveExchangeCard : null}
                        onShareExchangeCard={canManageWorkspaceSharing ? handleShareExchangeCard : null}
                        onEditExchangeCard={allowExchangeCardActions ? handleEditExchangeCard : null}
                        onExportExchangeCard={handleExportExchangeCard}
                        onViewExchangeCardRevisions={handleViewExchangeCardRevisions}
                        onPreparePrintJob={showWorkspaceActions ? handlePreparePrintJob : null}
                        onStartPrintJob={showWorkspaceActions ? handleStartPrintJob : null}
                        onConfirmPrintPickup={showWorkspaceActions ? handleConfirmPrintPickup : null}
                        onStartInitialRequest={showWorkspaceActions ? handleStartInitialRequest : null}
                        managedAssets={managedAssets}
                        managedAssetsLoading={managedAssetsLoading}
                        actionsDisabled={!allowExchangeCardActions || isSelectedThreadClosed || isWorkspaceDeactivated || isReadOnlySharedAccess}
                      />
                    )
                  ) : (
                    <Card sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          No message traffic yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          This workspace exists, but there are no correlation threads associated with it yet.
                        </Typography>
                      </CardContent>
                    </Card>
                  )}
                </Stack>
              </Box>
            ) : null}
          </Stack>
        </Box>
      </Box>
      <SharingDialog
        open={Boolean(shareDialog.open)}
        context={shareDialog}
        form={shareForm}
        onFormChange={setShareForm}
        onClose={() => closeShareDialog()}
        onSubmit={handleSubmitShare}
        busy={sharingBusy}
      />
      <Dialog
        open={deactivateDialogOpen}
        onClose={() => {
          if (!deactivating) {
            setDeactivateDialogOpen(false);
          }
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Deactivate Workspace?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will mark the workspace as deactivated and remove it from the active collaboration flow.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateDialogOpen(false)} disabled={deactivating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeactivateWorkspace}
            disabled={deactivating}
          >
            {deactivating ? 'Deactivating...' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={createThreadDialogOpen}
        onClose={() => {
          if (!threadActionLoading) {
            setCreateThreadDialogOpen(false);
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create New Thread</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Name the empty thread that will hold this workflow timeline.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="Thread name"
            value={newThreadTitle}
            onChange={(event) => setNewThreadTitle(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && newThreadTitle.trim() && !threadActionLoading) {
                handleCreateEmptyThread();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateThreadDialogOpen(false)} disabled={threadActionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateEmptyThread}
            disabled={threadActionLoading || !newThreadTitle.trim()}
          >
            {threadActionLoading ? 'Creating...' : 'Create Thread'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={initialRequestDialogOpen}
        onClose={() => setInitialRequestDialogOpen(false)}
        fullWidth
        maxWidth="xl"
      >
        <DialogTitle>Fill Initial Request</DialogTitle>
        <DialogContent
          dividers
          sx={{
            bgcolor: (theme) => alpha(theme.palette.background.default, 0.72),
          }}
        >
          <RequestWizard
            existingWorkspaceId={workspaceId}
            existingWorkspaceTitle={workspace?.title}
            existingCorrId={initialRequestCorrId}
            existingThreadTitle={(threadData || selectedThread)?.thread_title}
            existingIsCui={Boolean(workspace?.is_cui || (threadData || selectedThread)?.is_cui)}
            defaultRouteMode={(threadData || selectedThread)?.metadata?.route_mode}
            defaultTargetPartnerId={(threadData || selectedThread)?.metadata?.target_partner_id}
            onRequestCreated={handleInitialRequestCreated}
          />
        </DialogContent>
      </Dialog>
      <Dialog
        open={advertisementDialogOpen}
        onClose={() => {
          if (!advertising) {
            setAdvertisementDialogOpen(false);
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add Advertisement</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              select
              label="Advertisement type"
              value={advertisementKind}
              onChange={(event) => {
                setAdvertisementKind(event.target.value);
                setAdvertisementTargetId('');
              }}
              fullWidth
            >
              <MenuItem value="capability">Capability</MenuItem>
              <MenuItem value="asset">Asset</MenuItem>
            </TextField>
            <TextField
              select
              label={advertisementKind === 'asset' ? 'Asset' : 'Capability'}
              value={advertisementTargetId}
              onChange={(event) => setAdvertisementTargetId(event.target.value)}
              fullWidth
              disabled={managedCapabilitiesLoading || managedAssetsLoading}
            >
              {advertisementOptions.map((option) => {
                const value = advertisementKind === 'asset'
                  ? option.asset_id
                  : option.capability_definition_id;
                const label = advertisementKind === 'asset'
                  ? assetOptionLabel(option)
                  : capabilityOptionLabel(option);
                return (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                );
              })}
            </TextField>
            {!advertisementOptions.length ? (
              <Alert severity="info">
                No registered {advertisementKind === 'asset' ? 'assets' : 'capabilities'} are available for this request category.
              </Alert>
            ) : null}
            <TextField
              label="Provider notes"
              value={advertisementNotes}
              onChange={(event) => setAdvertisementNotes(event.target.value)}
              multiline
              minRows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdvertisementDialogOpen(false)} disabled={advertising}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitManualAdvertisement}
            disabled={advertising || !advertisementTargetId}
          >
            {advertising ? 'Adding...' : 'Add Advertisement'}
          </Button>
        </DialogActions>
      </Dialog>
    </AppTheme>
  );
}
